import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';

// CONTRACT_ABI for the new SingleRoundNumberGuessingGame contract
const CONTRACT_ABI = [
  "function createGame(address _invitedPlayer) external payable",
  "function joinGame(uint256 _gameId) external payable",
  "function submitGuess(uint256 _gameId, bytes32 _guessHash) external",
  "function revealGuess(uint256 _gameId, uint256 _guess, uint256 _nonce) external",
  "function cancelGame(uint256 _gameId) external",
  "function cancelExpiredGame(uint256 _gameId) external",
  "function forceFinishExpiredGame(uint256 _gameId) external",
  "function getGameCore(uint256 _gameId) external view returns (address,address,uint256,uint8,address,uint256,bool)",
  "function getGameData(uint256 _gameId) external view returns (uint256,uint256,uint256,bool,bool)",
  "function getPlayerGames(address _player) external view returns (uint256[])",
  "function createCommitHash(uint256 _guess, uint256 _nonce, address _player) external pure returns (bytes32)",
  "function nextGameId() external view returns (uint256)",
  "function owner() external view returns (address)",
  "function feeRecipient() external view returns (address)",
  "function platformFeePercent() external view returns (uint256)",
  "function withdrawFees() external",
  "function paused() external view returns (bool)",
  "function minBetAmount() external view returns (uint256)",
  "function maxBetAmount() external view returns (uint256)",
  "function gameTimeLimit() external view returns (uint256)",
  "event GameCreated(uint256 indexed gameId, address indexed player1, uint256 betAmount, address invitedPlayer)",
  "event GameJoined(uint256 indexed gameId, address indexed player2)",
  "event GameStarted(uint256 indexed gameId, uint256 targetNumber)",
  "event GuessSubmitted(uint256 indexed gameId, address indexed player)",
  "event GuessRevealed(uint256 indexed gameId, address indexed player, uint256 guess)",
  "event GameFinished(uint256 indexed gameId, address indexed winner, uint256 targetNumber, uint256 winnings)",
  "event GameCancelled(uint256 indexed gameId, address indexed canceller, string reason)"
];

// Network configurations
const NETWORKS = {
  avalanche: {
    chainId: 43114,
    chainIdHex: '0xA86A',
    name: 'Avalanche Network',
    currency: 'AVAX',
    contractAddress: '0xD601a238B027fD97A4132ae2463450868a9808d2',
    rpcUrls: ['https://api.avax.network/ext/bc/C/rpc'],
    blockExplorerUrls: ['https://snowtrace.io/'],
    icon: '🏔️'
  },
  ethereum: {
    chainId: 1,
    chainIdHex: '0x1',
    name: 'Ethereum Mainnet',
    currency: 'ETH',
    contractAddress: '0x90983a1222464Ee2f5C497c15184DEBB37BdDb95',
    rpcUrls: ['https://mainnet.infura.io/v3/'],
    blockExplorerUrls: ['https://etherscan.io/'],
    icon: '💎'
  },
  polygon: {
    chainId: 137,
    chainIdHex: '0x89',
    name: 'Polygon Network',
    currency: 'MATIC',
    contractAddress: '0xC3f39E45D627D7Ab4D75c41F51952343e5A3B6A2',
    rpcUrls: ['https://polygon-rpc.com'],
    blockExplorerUrls: ['https://polygonscan.com/'],
    icon: '🟣'
  }
};

const App = () => {
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [contract, setContract] = useState(null);
  const [account, setAccount] = useState('');
  const [balance, setBalance] = useState('0');
  const [loading, setLoading] = useState(false);
  const [games, setGames] = useState([]);
  const [availableGames, setAvailableGames] = useState([]);
  const [currentView, setCurrentView] = useState('dashboard');
  const [isConnected, setIsConnected] = useState(false);

  // Network state
  const [selectedNetwork, setSelectedNetwork] = useState(() => {
    try {
      const saved = localStorage.getItem('cyborg_selected_network');
      return saved && NETWORKS[saved] ? saved : 'avalanche';
    } catch {
      return 'avalanche';
    }
  });

  // Game creation state
  const [betAmount, setBetAmount] = useState('0.01');

  // Game play state
  const [gameGuesses, setGameGuesses] = useState(() => {
    try {
      const saved = localStorage.getItem('cyborg_game_guesses');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  }); // Store guesses per game
  const [gameNonces, setGameNonces] = useState(() => {
    try {
      const saved = localStorage.getItem('cyborg_game_nonces');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  }); // Store nonces per game

  // Owner state
  const [isOwner, setIsOwner] = useState(false);
  const [contractBalance, setContractBalance] = useState('0');
  const [loadingStates, setLoadingStates] = useState({});
  const [liveUpdatesEnabled, setLiveUpdatesEnabled] = useState(true);

  // Permanently hidden games (persisted in localStorage)
  const [hiddenGames, setHiddenGames] = useState(() => {
    try {
      const saved = localStorage.getItem('cyborg_hidden_games');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  // Function to hide games permanently
  const hideGamesPermanently = (gameIds) => {
    const newHiddenGames = [...hiddenGames, ...gameIds];
    setHiddenGames(newHiddenGames);
    localStorage.setItem('cyborg_hidden_games', JSON.stringify(newHiddenGames));
  };

  // Network helper functions
  const getCurrentNetwork = () => NETWORKS[selectedNetwork];
  const getCurrentContractAddress = () => getCurrentNetwork().contractAddress;
  const getCurrentCurrency = () => getCurrentNetwork().currency;

  // Switch network function
  const switchNetwork = async (networkKey) => {
    if (!NETWORKS[networkKey] || networkKey === selectedNetwork) return;

    try {
      setLoading(true);
      setSelectedNetwork(networkKey);
      localStorage.setItem('cyborg_selected_network', networkKey);

      // Reset bet amount to appropriate default for the network
      if (networkKey === 'polygon') {
        setBetAmount('0.01');
      } else {
        setBetAmount('0.01');
      }

      // The useEffect will handle reconnection
    } catch (error) {
      console.error('Error switching network:', error);
      alert('Failed to switch network: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkWalletConnection();
  }, []);

  useEffect(() => {
    if (contract && account && getCurrentContractAddress()) {
      loadPlayerGames();
      loadAvailableGames();
      setupEventListeners();
    }
    return () => {};
  }, [contract, account]);

  // Handle network switching
  useEffect(() => {
    if (isConnected) {
      // Clear games data when switching networks
      setGames([]);
      setAvailableGames([]);
      setContract(null);
      setProvider(null);
      setSigner(null);
      // Reconnect with new network after clearing state
      setTimeout(() => {
        connectWallet(false); // Don't request accounts again
      }, 100);
    }
  }, [selectedNetwork]);


  // Helper function to get MetaMask provider
  const getMetaMaskProvider = () => {
    if (typeof window === 'undefined') return null;

    // Case 1: window.ethereum is MetaMask directly
    if (window.ethereum?.isMetaMask && !window.ethereum?.isPhantom) {
      return window.ethereum;
    }

    // Case 2: Multiple providers array (when multiple wallets installed)
    if (window.ethereum?.providers) {
      const metamask = window.ethereum.providers.find(p => p.isMetaMask && !p.isPhantom);
      if (metamask) return metamask;
    }

    // Case 3: Check for MetaMask in window directly
    if (window.web3?.currentProvider?.isMetaMask) {
      return window.web3.currentProvider;
    }

    return null;
  };

  const checkWalletConnection = async () => {
    const ethereum = getMetaMaskProvider();
    if (ethereum) {
      try {
        const accounts = await ethereum.request({
          method: 'eth_accounts'
        });
        if (accounts.length > 0) {
          await connectWallet(false); // Don't request accounts, they're already connected
        }
      } catch (error) {
        console.error('Error checking wallet connection:', error);
      }
    }
  };

  const connectWallet = async (requestAccounts = true) => {
    try {
      const ethereum = getMetaMaskProvider();
      if (!ethereum) {
        alert('MetaMask not found! Please install MetaMask or disable other wallet extensions that might be interfering.');
        return;
      }

      if (!getCurrentContractAddress()) {
        alert('Contract address not set! Network configuration error.');
        return;
      }

      setLoading(true);

      if (requestAccounts) {
        await ethereum.request({ method: 'eth_requestAccounts' });
      }

      // Create provider with network detection disabled to prevent conflicts
      const provider = new ethers.providers.Web3Provider(ethereum, "any");
      const signer = provider.getSigner();
      const address = await signer.getAddress();
      const balance = await provider.getBalance(address);

      // Check if we're on the right network
      const currentNet = getCurrentNetwork();
      const network = await provider.getNetwork();
      if (network.chainId !== currentNet.chainId) {
        try {
          await ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: currentNet.chainIdHex }],
          });
        } catch (switchError) {
          // If the network doesn't exist, add it
          if (switchError.code === 4902) {
            try {
              await ethereum.request({
                method: 'wallet_addEthereumChain',
                params: [{
                  chainId: currentNet.chainIdHex,
                  chainName: currentNet.name,
                  rpcUrls: currentNet.rpcUrls,
                  nativeCurrency: {
                    name: currentNet.currency,
                    symbol: currentNet.currency,
                    decimals: 18
                  },
                  blockExplorerUrls: currentNet.blockExplorerUrls
                }]
              });
            } catch (addError) {
              alert(`Failed to add ${currentNet.name} to MetaMask`);
              setLoading(false);
              return;
            }
          } else {
            alert(`Please switch to ${currentNet.name} in MetaMask`);
            setLoading(false);
            return;
          }
        }
      }

      setProvider(provider);
      setSigner(signer);
      setAccount(address);
      setBalance(ethers.utils.formatEther(balance));
      setIsConnected(true);

      // Initialize contract
      const contract = new ethers.Contract(getCurrentContractAddress(), CONTRACT_ABI, signer);
      setContract(contract);

      // Check if user is owner
      try {
        const owner = await contract.owner();
        const isOwnerMatch = owner.toLowerCase() === address.toLowerCase();
        setIsOwner(isOwnerMatch);

        console.log('🔑 Owner check:', {
          contractOwner: owner,
          currentUser: address,
          isOwner: isOwnerMatch
        });

        // Get contract balance for all users (for transparency)
        const balance = await provider.getBalance(getCurrentContractAddress());
        setContractBalance(ethers.utils.formatEther(balance));

        if (isOwnerMatch) {
          console.log('👑 You are the contract owner!');
        }
      } catch (error) {
        console.error('Error checking owner status:', error);
        setIsOwner(false);
      }

      console.log('Wallet connected:', address);

    } catch (error) {
      console.error('Error connecting wallet:', error);
      alert('Failed to connect wallet: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const disconnectWallet = () => {
    setProvider(null);
    setSigner(null);
    setContract(null);
    setAccount('');
    setBalance('0');
    setIsConnected(false);
    setGames([]);
    setAvailableGames([]);
    setGameGuesses({});
    setGameNonces({});
    // Clear localStorage
    localStorage.removeItem('cyborg_game_guesses');
    localStorage.removeItem('cyborg_game_nonces');
    setIsOwner(false);
    setContractBalance('0');
    console.log('Wallet disconnected');
  };

  const setupEventListeners = () => {
    if (!contract) return;

    // Listen for game events and reload games
    contract.on('GameCreated', (gameId, player1, betAmount, invitedPlayer) => {
      console.log('Game created:', gameId.toString());
      setTimeout(() => {
        loadPlayerGames();
        loadAvailableGames();
      }, 2000);
    });

    contract.on('GameJoined', (gameId, player2) => {
      console.log('Game joined:', gameId.toString());
      setTimeout(() => {
        loadPlayerGames();
        loadAvailableGames();
      }, 2000);
    });

    contract.on('GameStarted', (gameId, targetNumber) => {
      console.log('Game started:', gameId.toString(), 'target:', targetNumber.toString());
      setTimeout(() => {
        loadPlayerGames();
        loadAvailableGames();
      }, 1000);
    });

    contract.on('GuessSubmitted', (gameId, player) => {
      console.log('Guess submitted for game:', gameId.toString(), 'by:', player);
      setTimeout(() => {
        loadPlayerGames();
        loadAvailableGames();
      }, 1000);
    });

    contract.on('GuessRevealed', (gameId, player, guess) => {
      console.log('Guess revealed for game:', gameId.toString(), 'Guess:', guess.toString());
      setTimeout(() => {
        loadPlayerGames();
        loadAvailableGames();
      }, 1000);
    });

    contract.on('GameFinished', (gameId, winner, targetNumber, winnings) => {
      console.log('Game finished:', gameId.toString(), 'Winner:', winner);
      setTimeout(() => {
        loadPlayerGames();
        loadAvailableGames();
      }, 2000);
    });

    contract.on('GameCancelled', (gameId, canceller, reason) => {
      console.log('Game cancelled:', gameId.toString(), 'Reason:', reason);
      setTimeout(() => {
        loadPlayerGames();
        loadAvailableGames();
      }, 1000);
    });

    // Cleanup listeners on unmount
    return () => {
      contract.removeAllListeners();
    };
  };

  const loadPlayerGames = async (retryCount = 0) => {
    if (!contract || !account) return;

    try {
      setLoadingStates(prev => ({...prev, playerGames: true}));
      console.log('Loading games for account:', account);

      // Add network verification
      const currentNet = getCurrentNetwork();
      const network = await provider.getNetwork();
      if (network.chainId !== currentNet.chainId) {
        console.warn('Network mismatch detected, skipping game load');
        return;
      }

      const gameIds = await contract.getPlayerGames(account);
      console.log('Found game IDs:', gameIds.map(id => id.toString()));

      if (gameIds.length === 0) {
        setGames([]);
        return;
      }

      const gamePromises = gameIds.map(async (gameId) => {
        try {
          // Get game core info
          const [player1, player2, betAmount, state, gameWinner, deadline, isVsContract] = await contract.getGameCore(gameId);

          // Get game data
          const [targetNumber, player1Guess, player2Guess, player1Revealed, player2Revealed] = await contract.getGameData(gameId);

          // Get game meta (optional - not available on all networks)
          let createdAt = 0;
          try {
            createdAt = await contract.getGameMeta(gameId);
          } catch (e) {
            // getGameMeta not available on this contract
            createdAt = Date.now() / 1000; // Use current time as fallback
          }

          return {
            id: gameId.toString(),
            player1,
            player2,
            betAmount: ethers.utils.formatEther(betAmount),
            state: parseInt(state),
            gameWinner,
            deadline: new Date(deadline.toNumber() * 1000),
            isVsContract,
            targetNumber: targetNumber.toString(),
            player1Guess: player1Guess.toString(),
            player2Guess: player2Guess.toString(),
            player1Revealed,
            player2Revealed,
            createdAt: new Date((typeof createdAt.toNumber === 'function' ? createdAt.toNumber() : createdAt) * 1000),
            // Single round game - no multiple rounds
            currentRound: 1,
            maxRounds: 1,
            player1Wins: 0,
            player2Wins: 0,
            // Round info for completed games display
            roundInfo: {
              completed: player1Revealed && player2Revealed,
              targetNumber: targetNumber.toString(),
              player1Guess: player1Guess.toString(),
              player2Guess: player2Guess.toString(),
              player1Revealed,
              player2Revealed
            }
          };
        } catch (error) {
          console.error('Error loading game', gameId.toString(), ':', error);
          return null;
        }
      });

      const gamesData = (await Promise.all(gamePromises)).filter(game => game !== null);
      console.log('Loaded games:', gamesData);
      setGames(gamesData);
      setLoadingStates(prev => ({...prev, playerGames: false}));
    } catch (error) {
      console.error('Error loading games:', error);
      setLoadingStates(prev => ({...prev, playerGames: false}));

      // Retry logic
      if (retryCount < 2) {
        console.log(`🔄 Retrying to load games... (attempt ${retryCount + 1})`);
        setTimeout(() => loadPlayerGames(retryCount + 1), 2000);
      }
    }
  };

  const loadAvailableGames = async (retryCount = 0) => {
    if (!contract || !account) return;

    try {
      setLoadingStates(prev => ({...prev, availableGames: true}));
      console.log('Loading available games for matchmaking...');

      // Add network verification
      const currentNet = getCurrentNetwork();
      const network = await provider.getNetwork();
      if (network.chainId !== currentNet.chainId) {
        console.warn('Network mismatch detected, skipping available games load');
        return;
      }

      // Get next game ID to know the range
      const nextGameId = await contract.nextGameId();
      const gamePromises = [];

      // Check games from 1 to nextGameId-1
      for (let i = 1; i < nextGameId.toNumber(); i++) {
        gamePromises.push(
          (async () => {
            try {
              // Get game core info
              const [player1, player2, betAmount, state, gameWinner, deadline, isVsContract] = await contract.getGameCore(i);

              // Get game meta for creation time (optional)
              let createdAt = 0;
              try {
                createdAt = await contract.getGameMeta(i);
              } catch (e) {
                // getGameMeta not available on this contract
                createdAt = Date.now() / 1000; // Use current time as fallback
              }

              // Only include open multiplayer games that are waiting for player 2
              console.log(`Game ${i}: player1=${player1}, account=${account}, isVsContract=${isVsContract}, state=${state}`);

              if (!isVsContract &&
                  state === 0 && // WaitingForPlayer2
                  (player2 === ethers.constants.AddressZero || player2 === '0x0000000000000000000000000000000000000000') &&
                  player1.toLowerCase() !== account.toLowerCase()) {

                return {
                  id: i.toString(),
                  player1,
                  player2,
                  betAmount: ethers.utils.formatEther(betAmount),
                  state: parseInt(state),
                  gameWinner,
                  deadline: new Date(deadline.toNumber() * 1000),
                  isVsContract,
                  currentRound: 1,
                  maxRounds: 1,
                  player1Wins: 0,
                  player2Wins: 0,
                  createdAt: new Date((typeof createdAt.toNumber === 'function' ? createdAt.toNumber() : createdAt) * 1000)
                };
              }
              return null;
            } catch (error) {
              return null;
            }
          })()
        );
      }

      const allGames = await Promise.all(gamePromises);
      const validGames = allGames.filter(game => game !== null);

      // Filter out posts older than 24 hours (to match contract join time limit)
      const twentyFourHoursAgo = Date.now() - (24 * 60 * 60 * 1000);
      const availableGames = validGames.filter(game => {
        const gameCreationTime = game.createdAt.getTime();
        const isWithin24Hours = gameCreationTime > twentyFourHoursAgo;

        if (!isWithin24Hours) {
          console.log(`Filtering out game ${game.id} - older than 24 hours`);
        }

        return isWithin24Hours;
      });

      console.log('Found available games for matchmaking (within 24 hours):', availableGames);
      setAvailableGames(availableGames);
      setLoadingStates(prev => ({...prev, availableGames: false}));

    } catch (error) {
      console.error('Error loading available games:', error);
      setLoadingStates(prev => ({...prev, availableGames: false}));

      // Retry logic
      if (retryCount < 2) {
        console.log(`🔄 Retrying to load available games... (attempt ${retryCount + 1})`);
        setTimeout(() => loadAvailableGames(retryCount + 1), 2000);
      }
    }
  };


  const createMultiplayerMission = async () => {
    if (!contract) return;

    try {
      setLoading(true);
      const betAmountWei = ethers.utils.parseEther(betAmount);

      // For multiplayer missions, use AddressZero for open recruitment
      const inviteAddr = ethers.constants.AddressZero;

      console.log('Creating multiplayer recruitment mission with bet:', betAmount, getCurrentCurrency());
      const tx = await contract.createGame(inviteAddr, {
        value: betAmountWei
      });

      console.log('Transaction sent:', tx.hash);
      await tx.wait();
      console.log('Multiplayer recruitment mission created successfully!');

      setBetAmount('0.01');
      setCurrentView('matchmaking');

      // Reload games after creation
      setTimeout(() => {
        loadPlayerGames();
        loadAvailableGames();
      }, 4000);

      // Show success message for multiplayer recruitment
      alert('⚡ Recruitment mission deployed to the agent pool! Other agents can now discover and join your mission.');

    } catch (error) {
      console.error('Error creating multiplayer mission:', error);
      alert('Failed to create recruitment mission: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const joinGame = async (gameId, betAmount) => {
    if (!contract) return;

    try {
      setLoading(true);
      const betAmountWei = ethers.utils.parseEther(betAmount);

      console.log('Joining game:', gameId);
      const tx = await contract.joinGame(gameId, {
        value: betAmountWei
      });

      await tx.wait();
      console.log('Joined game successfully!');
      alert('⚡ Mission joined! Preparing for code decryption phase...');

      // Auto-navigate to operations and reload
      setTimeout(() => {
        setCurrentView('dashboard');
        loadPlayerGames();
        loadAvailableGames();
      }, 2000);

    } catch (error) {
      console.error('Error joining game:', error);

      // Handle specific error cases
      if (error.message.includes('Game not accepting players')) {
        alert('⚡ Mission already started! Refreshing available missions...');
        setTimeout(() => {
          loadAvailableGames();
        }, 1000);
      } else if (error.message.includes('Incorrect bet amount')) {
        alert('💰 Stake amount mismatch! Please check the required amount.');
      } else if (error.message.includes('Cannot join your own game')) {
        alert('🚫 Cannot join your own mission! Create a different one or find another agent.');
      } else if (error.message.includes('Internal JSON-RPC error')) {
        alert('🔄 Network congestion detected. Please try again in a moment...');
      } else {
        alert('⚠️ Failed to join mission: ' + (error.reason || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const cancelGame = async (gameId) => {
    if (!contract) return;

    try {
      setLoading(true);
      console.log('Cancelling game:', gameId);

      const tx = await contract.cancelGame(gameId);
      console.log('Transaction sent:', tx.hash);
      await tx.wait();

      console.log('Game cancelled successfully!');
      alert('⚡ Mission cancelled! You have been refunded (minus cancellation fee).');

      // Reload games after cancellation
      setTimeout(() => {
        loadPlayerGames();
        loadAvailableGames();
      }, 2000);

    } catch (error) {
      console.error('Error cancelling game:', error);
      if (error.message.includes('Only creator can cancel')) {
        alert('🚫 Only the mission creator can cancel this game.');
      } else if (error.message.includes('Game already started')) {
        alert('⚠️ Cannot cancel - mission has already started!');
      } else {
        alert('Failed to cancel mission: ' + (error.reason || error.message));
      }
    } finally {
      setLoading(false);
    }
  };

  const submitGuess = async (gameId) => {
    if (!contract || !gameGuesses[gameId] || !gameNonces[gameId]) {
      alert('Please enter both a guess and nonce!');
      return;
    }

    // Extra check: ensure values are saved before submitting
    try {
      const savedGuesses = JSON.parse(localStorage.getItem('cyborg_game_guesses') || '{}');
      const savedNonces = JSON.parse(localStorage.getItem('cyborg_game_nonces') || '{}');

      if (!savedGuesses[gameId] || !savedNonces[gameId]) {
        console.log('🔧 Auto-saving values before submission...');
        savedGuesses[gameId] = gameGuesses[gameId];
        savedNonces[gameId] = gameNonces[gameId];
        localStorage.setItem('cyborg_game_guesses', JSON.stringify(savedGuesses));
        localStorage.setItem('cyborg_game_nonces', JSON.stringify(savedNonces));
      }
    } catch (e) {
      console.warn('Could not verify localStorage, continuing...', e);
    }

    console.log('🚀 Starting submission process for game:', gameId);
    console.log('📝 Guess:', gameGuesses[gameId], 'Nonce:', gameNonces[gameId]);
    console.log('👤 Account:', account);

    try {
      setLoading(true);

      // Get detailed game info
      console.log('🔍 Fetching game info...');
      const [player1, player2, betAmount, state, gameWinner, deadline, isVsContract] = await contract.getGameCore(gameId);
      const [targetNumber, player1Guess, player2Guess, player1Revealed, player2Revealed] = await contract.getGameData(gameId);

      console.log('🎮 Game Info:', {
        gameId,
        player1,
        player2,
        state: state.toString(),
        account
      });

      console.log('🎲 Game Data:', {
        gameId,
        targetNumber: targetNumber.toString(),
        player1Guess: player1Guess.toString(),
        player2Guess: player2Guess.toString(),
        player1Revealed,
        player2Revealed
      });

      // Verify game state (1 = WaitingForGuesses)
      if (state !== 1) {
        console.log('❌ Game not in correct state. Expected: 1 (WaitingForGuesses), Got:', state.toString());
        alert('🔄 Mission phase changed! Refreshing status...');
        setTimeout(() => {
          loadPlayerGames();
          loadAvailableGames();
        }, 1000);
        return;
      }

      // Determine if user is player1 or player2
      const isPlayer1 = player1.toLowerCase() === account.toLowerCase();
      const isPlayer2 = player2.toLowerCase() === account.toLowerCase();

      console.log('👥 Player check:', {
        isPlayer1,
        isPlayer2,
        player1,
        player2,
        account
      });

      if (!isPlayer1 && !isPlayer2) {
        console.log('❌ User is not a player in this game');
        alert('❌ You are not a player in this game!');
        return;
      }

      // SIMPLIFIED: Just try to submit and let the contract handle validation
      console.log('📡 Creating commit hash...');
      const guessHash = await contract.createCommitHash(
        parseInt(gameGuesses[gameId]),
        parseInt(gameNonces[gameId]),
        account
      );

      console.log('🔐 Commit hash created:', guessHash);
      console.log('📤 Submitting to contract...');

      const tx = await contract.submitGuess(gameId, guessHash);
      console.log('⏳ Transaction sent:', tx.hash);

      const receipt = await tx.wait();
      console.log('✅ Transaction confirmed:', receipt.transactionHash);

      // Extra backup save after successful submission
      try {
        const backupGuesses = {...gameGuesses};
        const backupNonces = {...gameNonces};
        localStorage.setItem('cyborg_game_guesses', JSON.stringify(backupGuesses));
        localStorage.setItem('cyborg_game_nonces', JSON.stringify(backupNonces));
        localStorage.setItem(`cyborg_backup_${gameId}`, JSON.stringify({
          guess: gameGuesses[gameId],
          nonce: gameNonces[gameId],
          timestamp: Date.now()
        }));
      } catch (e) {
        console.warn('Backup save failed:', e);
      }

      alert(`⚡ Code transmitted successfully! Sequence (${gameGuesses[gameId]}) and key (${gameNonces[gameId]}) logged. Await decryption phase to determine neural activation!`);

      // Force immediate refresh
      setTimeout(() => {
        loadPlayerGames();
        loadAvailableGames();
      }, 1000);

    } catch (error) {
      console.error('💥 Submission error:', error);
      console.error('💥 Error details:', {
        message: error.message,
        code: error.code,
        reason: error.reason,
        data: error.data
      });

      // Handle specific error cases with better recovery
      if (error.message.includes('Already submitted') || error.message.includes('already revealed')) {
        console.log('🔄 Already submitted - auto-fixing state');
        alert('⚡ Code already transmitted! Auto-syncing game state...');
        // Force immediate refresh
        loadPlayerGames();
        loadAvailableGames();
      } else if (error.message.includes('Not accepting guesses') || error.message.includes('Not in reveal phase')) {
        console.log('🔄 Wrong phase - checking current game state');

        // Check current game state to give specific feedback
        (async () => {
          try {
            const [, , , currentState] = await contract.getGameCore(gameId);
            console.log('Current game state:', currentState.toString());

            if (currentState === 0) {
              alert('⏳ Game is still waiting for the second player to join.');
            } else if (currentState === 2) {
              alert('⚡ Both players have already submitted! The game moved to the decryption phase. Refreshing...');
            } else if (currentState === 3) {
              alert('🏁 Game has already finished! Refreshing...');
            } else if (currentState === 4) {
              alert('❌ Game was cancelled. Refreshing...');
            } else {
              alert('🔄 Game state changed unexpectedly. Refreshing...');
            }
          } catch (stateError) {
            console.error('Could not check current state:', stateError);
            alert('🔄 Game phase changed! Auto-syncing...');
          }

          // Force immediate refresh
          loadPlayerGames();
          loadAvailableGames();
        })();
      } else if (error.message.includes('Internal JSON-RPC error') || error.message.includes('network')) {
        console.log('🌐 Network error - will auto-retry');
        alert('🌐 Network issue detected. Auto-retrying in 3 seconds...');
        setTimeout(() => {
          loadPlayerGames();
          loadAvailableGames();
        }, 3000);
      } else if (error.message.includes('user rejected') || error.message.includes('denied')) {
        alert('❌ Transaction cancelled by user.');
      } else {
        console.error('Submission error details:', error);
        alert('⚠️ Code transmission failed: ' + (error.reason || error.message || 'Unknown error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const revealGuess = async (gameId) => {
    if (!contract || !gameGuesses[gameId] || !gameNonces[gameId]) {
      // Try to recover from backup before failing
      try {
        const backup = localStorage.getItem(`cyborg_backup_${gameId}`);
        if (backup) {
          const { guess, nonce } = JSON.parse(backup);
          updateGameGuess(gameId, guess);
          updateGameNonce(gameId, nonce);
          alert(`🔄 Auto-recovered values: Code ${guess}, Key ${nonce}. Click Decrypt again!`);
          return;
        }
      } catch (e) {
        console.warn('Backup recovery failed:', e);
      }

      alert('Please enter your original guess and nonce!');
      return;
    }

    try {
      setLoading(true);

      // First check current game state to prevent errors
      console.log('Checking game state before reveal...');
      const [, , , state] = await contract.getGameCore(gameId);

      if (state !== 2) { // Not in "WaitingForReveal" state
        alert('🔄 Mission phase changed! Refreshing status...');
        setTimeout(() => {
          loadPlayerGames();
          loadAvailableGames();
        }, 1000);
        return;
      }

      console.log('Revealing guess for game:', gameId);

      const tx = await contract.revealGuess(
        gameId,
        parseInt(gameGuesses[gameId]),
        parseInt(gameNonces[gameId])
      );

      await tx.wait();
      alert('🤖 Code decrypted! Neural pathways analyzed. Check cyborg response below.');

        // Clear the guess and nonce for this game from both state and localStorage
      setGameGuesses(prev => {
        const updated = {...prev};
        delete updated[gameId];
        localStorage.setItem('cyborg_game_guesses', JSON.stringify(updated));
        return updated;
      });
      setGameNonces(prev => {
        const updated = {...prev};
        delete updated[gameId];
        localStorage.setItem('cyborg_game_nonces', JSON.stringify(updated));
        return updated;
      });

      // Also clear the backup
      try {
        localStorage.removeItem(`cyborg_backup_${gameId}`);
      } catch (e) {
        console.warn('Could not clear backup:', e);
      }

    } catch (error) {
      console.error('Error revealing guess:', error);

      // Handle specific error cases with better recovery
      if (error.message.includes('Already revealed')) {
        alert('🔓 Code already decrypted! Auto-syncing game state...');
        // Force immediate refresh
        loadPlayerGames();
        loadAvailableGames();
      } else if (error.message.includes('Not in reveal phase')) {
        alert('🔄 Game phase changed! Auto-syncing...');
        // Force immediate refresh
        loadPlayerGames();
        loadAvailableGames();
      } else if (error.message.includes('Invalid reveal') || error.message.includes('hash mismatch')) {
        alert('❌ Code/Key mismatch! Check your saved values or clear them to start fresh.');
        alert('🔄 Auto-recovered values failed. Please enter your values manually.');
      } else if (error.message.includes('Internal JSON-RPC error') || error.message.includes('network')) {
        alert('🌐 Network issue detected. Auto-retrying in 3 seconds...');
        setTimeout(() => {
          loadPlayerGames();
          loadAvailableGames();
        }, 3000);
      } else if (error.message.includes('user rejected') || error.message.includes('denied')) {
        alert('❌ Transaction cancelled by user.');
      } else {
        console.error('Reveal error details:', error);
        alert('⚠️ Code decryption failed: ' + (error.reason || error.message || 'Unknown error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const getGameStateText = (state) => {
    const states = {
      0: 'Seeking Partner',
      1: 'Phase 1: Code Input',
      2: 'Phase 2: Decryption',
      3: 'Phase 3: Complete',
      4: 'Aborted'
    };
    return states[state] || 'Unknown Status';
  };

  const getPhaseProgress = (state) => {
    const phases = {
      0: 'Preparation',
      1: '1 of 3',
      2: '2 of 3',
      3: '3 of 3',
      4: 'Cancelled'
    };
    return phases[state] || '?';
  };

  const updateGameGuess = (gameId, value) => {
    setGameGuesses(prev => {
      const updated = {...prev, [gameId]: value};
      localStorage.setItem('cyborg_game_guesses', JSON.stringify(updated));
      return updated;
    });
  };

  const updateGameNonce = (gameId, value) => {
    setGameNonces(prev => {
      const updated = {...prev, [gameId]: value};
      localStorage.setItem('cyborg_game_nonces', JSON.stringify(updated));
      return updated;
    });
  };

  const generateRandomNonce = (gameId) => {
    const randomNonce = Math.floor(Math.random() * 1000000).toString();
    updateGameNonce(gameId, randomNonce);
  };

  const generateRandomGuess = (gameId) => {
    const randomGuess = Math.floor(Math.random() * 100 + 1).toString();
    updateGameGuess(gameId, randomGuess);
  };



  const withdrawFees = async () => {
    if (!contract) {
      alert('Contract not loaded!');
      return;
    }

    try {
      setLoading(true);
      console.log('🔍 Checking withdrawal permissions...');

      // Double-check owner status
      const contractOwner = await contract.owner();
      console.log('Contract owner:', contractOwner);
      console.log('Current account:', account);
      console.log('Is owner match:', contractOwner.toLowerCase() === account.toLowerCase());

      if (contractOwner.toLowerCase() !== account.toLowerCase()) {
        alert(`❌ Access denied! You are not the contract owner.\n\nContract Owner: ${contractOwner}\nYour Address: ${account}`);
        return;
      }

      // Check contract balance
      const contractBalance = await provider.getBalance(getCurrentContractAddress());
      const balanceEth = ethers.utils.formatEther(contractBalance);
      console.log('Contract balance:', balanceEth, getCurrentCurrency());

      if (contractBalance.eq(0)) {
        alert(`⚠️ No fees to withdraw. Contract balance is 0 ${getCurrentCurrency()}.`);
        return;
      }

      // Check if there are withdrawable fees vs locked game stakes
      try {
        const platformFeePercent = await contract.platformFeePercent();
        console.log('Platform fee percent:', platformFeePercent.toString());
      } catch (e) {
        console.warn('Could not get platform fee percent:', e);
      }

      console.log(`💰 Attempting to withdraw ${balanceEth} ${getCurrentCurrency()} in fees...`);

      // Try to estimate gas first
      try {
        const gasEstimate = await contract.estimateGas.withdrawFees();
        console.log('Gas estimate:', gasEstimate.toString());
      } catch (gasError) {
        console.error('Gas estimation failed:', gasError);

        if (gasError.message.includes('execution reverted')) {
          alert('⚠️ Transaction would fail. Possible reasons:\n\n1. No withdrawable fees (all funds locked in active games)\n2. Contract owner restrictions\n3. Contract implementation issue\n\nContract balance: ' + balanceEth + ' ' + getCurrentCurrency());
          return;
        }
      }

      const tx = await contract.withdrawFees();
      console.log('Transaction sent:', tx.hash);

      await tx.wait();
      console.log('Transaction confirmed!');

      alert(`🎉 Successfully withdrew fees!\n\nTransaction: ${tx.hash}`);

      // Update contract balance
      const newBalance = await provider.getBalance(getCurrentContractAddress());
      setContractBalance(ethers.utils.formatEther(newBalance));

    } catch (error) {
      console.error('💥 Withdrawal error:', error);

      let errorMessage = 'Failed to withdraw fees: ';

      if (error.message.includes('execution reverted')) {
        errorMessage += 'Transaction reverted. This usually means:\n\n' +
          '• No withdrawable fees available\n' +
          '• All funds are locked in active games\n' +
          '• Contract owner restrictions';
      } else if (error.message.includes('user denied')) {
        errorMessage = 'Transaction cancelled by user.';
      } else if (error.message.includes('insufficient funds')) {
        errorMessage += `Insufficient ${getCurrentCurrency()} for gas fees.`;
      } else {
        errorMessage += error.message || 'Unknown error';
      }

      alert('❌ ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getGameActions = (game) => {
    const gameState = parseInt(game.state);
    const isPlayer1 = game.player1.toLowerCase() === account.toLowerCase();
    const isPlayer2 = game.player2.toLowerCase() === account.toLowerCase();
    const gameId = game.id;
    const roundInfo = game.roundInfo;

    if (gameState === 0) { // Waiting for Player 2
      if (!game.isVsContract && !isPlayer1 && (game.player2 === ethers.constants.AddressZero || isPlayer2)) {
        return (
          <button
            onClick={() => joinGame(gameId, game.betAmount)}
            disabled={loading}
            className="btn btn-primary"
          >
            {loading ? 'Joining Mission...' : 'Join Mission'}
          </button>
        );
      }
      // Check if this is a direct invitation
      const isDirectInvite = !game.isVsContract && game.player2 !== ethers.constants.AddressZero;

      return (
        <div className="waiting-section">
          <div className="waiting-text">
            {game.isVsContract ? 'AI System connecting...' :
             isDirectInvite ? `Awaiting response from invited agent...` : 'Awaiting partner agent...'}
          </div>

          {/* Direct invitation helper */}
          {isPlayer1 && isDirectInvite && (
            <div className="direct-invitation-helper">
              <h4>📧 Direct Challenge Sent</h4>
              <div className="invitation-details">
                <div className="invited-agent-info">
                  <p><strong>Invited Agent:</strong></p>
                  <div className="agent-address">{game.player2}</div>
                </div>
                <p><strong>Stakes:</strong> {game.betAmount} {getCurrentCurrency()}</p>
                <p><strong>Expires:</strong> {new Date(game.deadline).toLocaleString()}</p>
              </div>

              <div className="notification-warning">
                <p>⚠️ <strong>Important:</strong> The invited agent won't automatically know about this challenge!</p>
                <p>You need to notify them manually using the tools below:</p>
              </div>

              <div className="invitation-actions">
                <button
                  onClick={() => {
                    const inviteLink = `${window.location.origin}${window.location.pathname}#game-${game.id}`;
                    const message = `🎮 CYBORG RESURRECTION CHALLENGE 🎮\n\n` +
                      `You've been challenged to a neural decryption mission!\n\n` +
                      `🔥 Mission #${game.id}\n` +
                      `💰 Stakes: ${game.betAmount} ${getCurrentCurrency()}\n` +
                      `⏰ Expires: ${new Date(game.deadline).toLocaleString()}\n\n` +
                      `🔗 Accept Challenge: ${inviteLink}\n\n` +
                      `Instructions:\n` +
                      `1. Switch MetaMask to: ${game.player2}\n` +
                      `2. Visit the link above\n` +
                      `3. Find Mission #${game.id} in your Operations\n` +
                      `4. Click "Join Mission" to accept`;

                    navigator.clipboard.writeText(message);
                    alert('📧 Challenge invitation copied to clipboard! Send this to the other player.');
                  }}
                  className="btn btn-primary"
                >
                  📧 Copy Invitation Message
                </button>

                <button
                  onClick={() => {
                    const gameUrl = `${window.location.origin}${window.location.pathname}#game-${game.id}`;
                    navigator.clipboard.writeText(gameUrl);
                    alert('🔗 Game link copied! Share this with the invited player.');
                  }}
                  className="btn btn-secondary"
                >
                  🔗 Copy Game Link
                </button>

                <button
                  onClick={() => {
                    const tweetText = `🎮 I challenge ${game.player2.slice(0, 6)}...${game.player2.slice(-4)} to a Cyborg Resurrection mission! 🤖\n\nStakes: ${game.betAmount} ${getCurrentCurrency()}\nMission #${game.id}\n\n#CyborgResurrection #BlockchainGaming #${getCurrentCurrency()}`;
                    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
                    window.open(tweetUrl, '_blank');
                  }}
                  className="btn btn-secondary"
                >
                  🐦 Share on Twitter
                </button>
              </div>

              <div className="waiting-tips">
                <h5>💡 Pro Tips:</h5>
                <ul>
                  <li>Send the invitation message via Discord, Telegram, or email</li>
                  <li>Make sure they have the correct wallet address selected</li>
                  <li>They need to visit this site and look for Mission #{game.id}</li>
                  <li>Game will auto-expire in 1 hour if not accepted</li>
                </ul>
              </div>
            </div>
          )}

          {/* Regular waiting for open recruitment */}
          {isPlayer1 && !game.isVsContract && game.player2 === ethers.constants.AddressZero && (
            <div className="cancel-option">
              <button
                className="btn btn-cancel"
                onClick={() => cancelGame(game.id)}
                disabled={loading}
              >
                🚫 Cancel Mission
              </button>
              <small className="cancel-help">
                Note: Cancellation includes a small fee (0.5%). Mission auto-expires after 24 hours if no agent joins.
              </small>
            </div>
          )}
        </div>
      );
    }

    else if (gameState === 1) { // Waiting for Guesses
      if (isPlayer1 || isPlayer2) {

        // Check if this player has already submitted using hash info
        const hasSubmitted = roundInfo?.hashInfo &&
          ((isPlayer1 && roundInfo.hashInfo.player1HasSubmitted) ||
           (isPlayer2 && roundInfo.hashInfo.player2HasSubmitted));

        if (hasSubmitted) {
          // Player has already submitted - show waiting state
          return (
            <div className="waiting-section">
              <div className="waiting-text">
                ⚡ Code transmitted! Awaiting {isPlayer1 ? 'Agent 2' : 'Agent 1'} submission...
              </div>
              <div className="submission-status">
                <p><strong>Your Status:</strong> ✅ Code Submitted</p>
                <p><strong>Opponent Status:</strong> {
                  (isPlayer1 && roundInfo.hashInfo.player2HasSubmitted) ||
                  (isPlayer2 && roundInfo.hashInfo.player1HasSubmitted)
                    ? '✅ Code Submitted - Moving to decryption...'
                    : '⏳ Awaiting submission...'
                }</p>
              </div>

              {/* Multi-wallet helper */}
              <div className="multi-wallet-helper">
                <h4>🔄 Playing with Multiple Wallets?</h4>
                <div className="wallet-info">
                  <p><strong>Agent 1:</strong> {game.player1.slice(0, 10)}...{game.player1.slice(-6)}</p>
                  <p><strong>Agent 2:</strong> {game.player2.slice(0, 10)}...{game.player2.slice(-6)}</p>
                  <p><strong>Current Wallet:</strong> {account.slice(0, 10)}...{account.slice(-6)}</p>
                </div>

                <div className="coordination-help">
                  <p>💡 <strong>If you control both wallets:</strong></p>
                  <ol>
                    <li>Switch to the other wallet in MetaMask</li>
                    <li>Refresh this page or click "Force Sync"</li>
                    <li>Submit the opponent's guess to continue</li>
                  </ol>

                  <div className="quick-actions">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`Game #${game.id} needs ${isPlayer1 ? 'Agent 2' : 'Agent 1'} to submit`);
                        alert('📋 Copied game status to clipboard!');
                      }}
                      className="btn btn-secondary btn-small"
                    >
                      📋 Copy Status
                    </button>

                    <button
                      onClick={() => {
                        const otherWallet = isPlayer1 ? game.player2 : game.player1;
                        const instructions = `🎮 Game #${game.id} Instructions:\n\n🔄 Switch MetaMask to: ${otherWallet}\n⚡ Action needed: Submit code sequence\n🎯 Stakes: ${game.betAmount} ${getCurrentCurrency()}\n\n🔗 Game URL: ${window.location.href}`;
                        navigator.clipboard.writeText(instructions);
                        alert('📧 Copied detailed instructions to clipboard!');
                      }}
                      className="btn btn-primary btn-small"
                    >
                      📧 Copy Instructions
                    </button>
                  </div>

                  <div className="auto-switch-reminder">
                    <p><small>⏰ <strong>Reminder:</strong> Game expires at {new Date(game.deadline).toLocaleString()}</small></p>
                  </div>
                </div>
              </div>
            </div>
          );
        }
        return (
          <div className="guess-form">
            {game.isVsContract && isPlayer1 && (
              <div className="vs-contract-info">
                <p>🤖 Infiltrating AI Defense System! Stakes: {game.betAmount} {getCurrentCurrency()}</p>
                <p>Decryption Phase - Single Shot Battle</p>
                <p>Battle against the AI Defense System</p>
              </div>
            )}

            {!game.isVsContract && (
              <div className="multiplayer-info">
                <div className="player-roles">
                  <p><strong>👤 You are:</strong> {isPlayer1 ? 'Agent 1' : 'Agent 2'} ({account.slice(0, 6)}...{account.slice(-4)})</p>
                  <p><strong>🎯 Opponent:</strong> {isPlayer1 ? 'Agent 2' : 'Agent 1'} ({(isPlayer1 ? game.player2 : game.player1).slice(0, 6)}...{(isPlayer1 ? game.player2 : game.player1).slice(-4)})</p>
                </div>
              </div>
            )}
            <div className="step-indicator">
              <div className="phase-progress">
                <span className="progress-text">Mission Progress: Phase 1 of 3</span>
              </div>
              <div className="step active">
                <span className="step-number">1</span>
                <span className="step-text">Input Code Sequence</span>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <span className="step-number">2</span>
                <span className="step-text">Decrypt & Verify</span>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <span className="step-number">3</span>
                <span className="step-text">Neural Activation</span>
              </div>
            </div>
            <div className="help-text">
              <p><strong>Phase 1 of 3:</strong> Enter your code sequence (1-100) to breach the cyborg's neural lock.</p>
              <p><small>🔐 The encryption key keeps your attempt secret until both agents submit.</small></p>
              <p><small>📋 <strong>What's Next:</strong> Phase 2 (Decrypt) → Phase 3 (Neural Activation & Results)</small></p>
              <p><small>💡 <strong>Tip:</strong> Use "Auto-Fill" to generate both values instantly, or "Quick Play" to complete the entire game automatically!</small></p>
            </div>

            {/* Critical Warning */}
            <div className="critical-warning">
              <div className="warning-icon">⚠️</div>
              <div className="warning-content">
                <h4>🚨 CRITICAL: Write Down These Values!</h4>
                <p><strong>You MUST record your Code Sequence and Encryption Key!</strong></p>
                <p>You'll need both values in Phase 2 to complete the mission. If you lose them, the mission will fail and you'll lose your {getCurrentCurrency()} stake.</p>
                <div className="warning-checklist">
                  <p>✍️ Write down: <strong>Code Sequence</strong> (1-100)</p>
                  <p>✍️ Write down: <strong>Encryption Key</strong> (random number)</p>
                </div>
              </div>
            </div>

            <div className="form-row">
              <input
                type="number"
                min="1"
                max="100"
                placeholder="Code sequence (1-100)"
                value={gameGuesses[gameId] || ''}
                onChange={(e) => updateGameGuess(gameId, e.target.value)}
                className="form-input small"
              />
              <button
                onClick={() => generateRandomGuess(gameId)}
                className="btn btn-secondary small"
              >
                Gen Code
              </button>
              <button
                onClick={() => generateRandomNonce(gameId)}
                className="btn btn-secondary small"
              >
                Gen Key
              </button>
            </div>
            <input
              type="number"
              placeholder="Encryption key (or click Gen Key)"
              value={gameNonces[gameId] || ''}
              onChange={(e) => updateGameNonce(gameId, e.target.value)}
              className="form-input"
            />
            <button
              onClick={() => submitGuess(gameId)}
              disabled={loading || !gameGuesses[gameId] || !gameNonces[gameId]}
              className="btn btn-primary"
            >
              {loading ? 'Transmitting Code...' : 'Transmit Encrypted Code'}
            </button>

            {!game.isVsContract && (
              <div className="coordination-reminder">
                <small>📄 <strong>Reminder:</strong> Both agents must submit before anyone can decrypt</small>
              </div>
            )}
          </div>
        );
      }
    }

    else if (gameState === 2) { // Waiting for Reveal
      if (isPlayer1 || isPlayer2) {
        // Check if this player has already revealed
        const hasRevealed = roundInfo &&
          ((isPlayer1 && roundInfo.player1Revealed) ||
           (isPlayer2 && roundInfo.player2Revealed));

        const opponentRevealed = roundInfo &&
          ((isPlayer1 && roundInfo.player2Revealed) ||
           (isPlayer2 && roundInfo.player1Revealed));

        if (hasRevealed && !opponentRevealed) {
          // Player has revealed, waiting for opponent
          return (
            <div className="waiting-reveal-section">
              <div className="step-indicator">
                <div className="phase-progress">
                  <span className="progress-text">Mission Progress: Phase 2 of 3</span>
                </div>
                <div className="step completed">
                  <span className="step-number">✓</span>
                  <span className="step-text">Code Transmitted</span>
                </div>
                <div className="step-arrow">→</div>
                <div className="step completed">
                  <span className="step-number">✓</span>
                  <span className="step-text">You Decrypted</span>
                </div>
                <div className="step-arrow">→</div>
                <div className="step pending">
                  <span className="step-number">⏳</span>
                  <span className="step-text">Awaiting Opponent</span>
                </div>
              </div>

              <div className="waiting-opponent">
                <div className="opponent-status">
                  <h4>🔄 Waiting for Opponent to Decrypt</h4>
                  <p>✅ <strong>Your Status:</strong> Code decrypted successfully!</p>
                  <p>⏳ <strong>Opponent Status:</strong> {game.isVsContract ? 'AI System processing...' : 'Rival agent needs to decrypt their code'}</p>
                  <div className="reveal-details">
                    <p><strong>Your Revealed Code:</strong> {isPlayer1 ? roundInfo.player1Guess : roundInfo.player2Guess}</p>
                    <p><strong>Waiting for:</strong> {game.isVsContract ? 'AI System to complete analysis' : 'Opponent to reveal their code sequence'}</p>
                  </div>
                </div>

                {/* Auto-refresh indicator */}
                <div className="auto-refresh-waiting">
                  <div className="pulse-dot"></div>
                  <small>Auto-checking every 15 seconds for opponent action...</small>
                </div>

                {/* Timeout warning */}
                <div className="timeout-info">
                  <p><small>⏰ <strong>Game Timeout:</strong> {new Date(game.deadline).toLocaleString()}</small></p>
                  <p><small>🏆 <strong>Auto-Win:</strong> You'll win automatically if opponent doesn't respond by deadline</small></p>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div className="reveal-form">
            <div className="step-indicator">
              <div className="phase-progress">
                <span className="progress-text">Mission Progress: Phase 2 of 3</span>
              </div>
              <div className="step completed">
                <span className="step-number">✓</span>
                <span className="step-text">Code Transmitted</span>
              </div>
              <div className="step-arrow">→</div>
              <div className="step active">
                <span className="step-number">2</span>
                <span className="step-text">Decrypt & Verify</span>
              </div>
              <div className="step-arrow">→</div>
              <div className="step">
                <span className="step-number">3</span>
                <span className="step-text">Neural Activation</span>
              </div>
            </div>
            <div className="help-text">
              <p><strong>Phase 2 of 3:</strong> Decrypt your code to activate the cyborg's neural pathways!</p>
              <p><small>🔓 Values below are auto-saved from Phase 1 - just verify and decrypt!</small></p>
              <p><small>📋 <strong>What's Next:</strong> Phase 3 will show neural activation results automatically</small></p>
              <p><small>⚡ Single Shot - Winner Takes All</small></p>
              <p><small>💾 <strong>Recovery:</strong> Values restored from browser storage if page refreshed!</small></p>
              {(!gameGuesses[gameId] || !gameNonces[gameId]) && (
                <div className="missing-values-warning">
                  <p><strong>⚠️ Missing saved values!</strong> Use Auto-Recovery below or enter your original values:</p>
                  <div className="recovery-buttons">
                    <button
                      onClick={() => {
                        // Try to recover from any stored data first
                        const savedGuesses = localStorage.getItem('cyborg_game_guesses');
                        const savedNonces = localStorage.getItem('cyborg_game_nonces');

                        if (savedGuesses && savedNonces) {
                          try {
                            const guesses = JSON.parse(savedGuesses);
                            const nonces = JSON.parse(savedNonces);
                            if (guesses[gameId] && nonces[gameId]) {
                              setGameGuesses(guesses);
                              setGameNonces(nonces);
                              alert(`🔄 Recovered: Code ${guesses[gameId]}, Key ${nonces[gameId]}`);
                              return;
                            }
                          } catch (e) {
                            console.log('Recovery failed, will generate new');
                          }
                        }

                        // If recovery fails, generate new values
                        const randomGuess = Math.floor(Math.random() * 100 + 1).toString();
                        const randomNonce = Math.floor(Math.random() * 1000000).toString();
                        updateGameGuess(gameId, randomGuess);
                        updateGameNonce(gameId, randomNonce);
                        alert(`🎲 Generated: Code ${randomGuess}, Key ${randomNonce}. Ready to transmit!`);
                      }}
                      className="btn btn-primary btn-small"
                    >
                      🔄 Auto-Recovery
                    </button>
                  </div>
                </div>
              )}

              {/* Status indicator for reveals */}
              {roundInfo && (
                <div className="reveal-status">
                  <h4>🔍 Decryption Status</h4>
                  <div className="status-grid">
                    <div className="status-item">
                      <span className="status-label">Your Status:</span>
                      <span className={`status-value ${(isPlayer1 && roundInfo.player1Revealed) || (isPlayer2 && roundInfo.player2Revealed) ? 'completed' : 'pending'}`}>
                        {(isPlayer1 && roundInfo.player1Revealed) || (isPlayer2 && roundInfo.player2Revealed) ? '✅ Decrypted' : '⏳ Need to Decrypt'}
                      </span>
                    </div>
                    <div className="status-item">
                      <span className="status-label">Opponent Status:</span>
                      <span className={`status-value ${(isPlayer1 && roundInfo.player2Revealed) || (isPlayer2 && roundInfo.player1Revealed) ? 'completed' : 'pending'}`}>
                        {(isPlayer1 && roundInfo.player2Revealed) || (isPlayer2 && roundInfo.player1Revealed) ? '✅ Decrypted' : '⏳ Waiting...'}
                      </span>
                    </div>
                  </div>
                  {((isPlayer1 && roundInfo.player1Revealed && !roundInfo.player2Revealed) ||
                    (isPlayer2 && roundInfo.player2Revealed && !roundInfo.player1Revealed)) && (
                    <p className="waiting-note">🕐 Waiting for {game.isVsContract ? 'AI System' : 'opponent'} to complete decryption...</p>
                  )}
                </div>
              )}
            </div>
            <input
              type="number"
              min="1"
              max="100"
              placeholder="Original code sequence (1-100)"
              value={gameGuesses[gameId] || ''}
              onChange={(e) => updateGameGuess(gameId, e.target.value)}
              className="form-input"
            />
            <input
              type="number"
              placeholder="Original encryption key"
              value={gameNonces[gameId] || ''}
              onChange={(e) => updateGameNonce(gameId, e.target.value)}
              className="form-input"
            />
            <button
              onClick={() => revealGuess(gameId)}
              disabled={loading || !gameGuesses[gameId] || !gameNonces[gameId] || (roundInfo && ((isPlayer1 && roundInfo.player1Revealed) || (isPlayer2 && roundInfo.player2Revealed)))}
              className="btn btn-primary"
            >
              {loading ? 'Decrypting...' :
               (roundInfo && ((isPlayer1 && roundInfo.player1Revealed) || (isPlayer2 && roundInfo.player2Revealed))) ?
               '✅ Already Decrypted' : 'Decrypt & Activate'}
            </button>
          </div>
        );
      }
    }

    else if (gameState === 3) { // Finished
      const isWinner = game.gameWinner && game.gameWinner.toLowerCase() === account.toLowerCase();
      const isTie = !game.gameWinner || game.gameWinner === ethers.constants.AddressZero;
      const isContractWinner = game.isVsContract && game.gameWinner && game.gameWinner.toLowerCase() === getCurrentContractAddress().toLowerCase();

      return (
        <div className="game-result">
          <div className="step-indicator completed-mission">
            <div className="phase-progress">
              <span className="progress-text completed">✅ Mission Complete: Phase 3 of 3</span>
            </div>
            <div className="step completed">
              <span className="step-number">✓</span>
              <span className="step-text">Code Transmitted</span>
            </div>
            <div className="step-arrow">→</div>
            <div className="step completed">
              <span className="step-number">✓</span>
              <span className="step-text">Decrypted</span>
            </div>
            <div className="step-arrow">→</div>
            <div className="step completed">
              <span className="step-number">✓</span>
              <span className="step-text">Neural Activation</span>
            </div>
          </div>

          {isTie ? (
            <div className="tie-result">⚡ Neural stalemate achieved!</div>
          ) : isWinner ? (
            <div className="win-result">🤖 CYBORG AWAKENED! Mission successful!</div>
          ) : isContractWinner ? (
            <div className="lose-result">🔒 AI defenses held! System secured!</div>
          ) : (
            <div className="lose-result">💀 Mission failed! Code rejected!</div>
          )}
          <div className="game-details">
            <p><strong>Game Result:</strong> Single Shot Complete</p>
            {roundInfo && roundInfo.completed && (
              <>
                <p><strong>Final Neural Code:</strong> {roundInfo.targetNumber}</p>
                <p><strong>Your Sequence:</strong> {isPlayer1 ? roundInfo.player1Guess : roundInfo.player2Guess}</p>
                <p><strong>Rival Sequence:</strong> {isPlayer1 ? roundInfo.player2Guess : roundInfo.player1Guess}</p>
              </>
            )}
          </div>
        </div>
      );
    }

    return null;
  };

  // Show setup message if contract address not configured
  if (!getCurrentContractAddress()) {
    return (
      <div className="app">
        <div className="setup-message">
          <h1>⚠️ Network Configuration Error</h1>
          <div className="setup-instructions">
            <h2>Contract address missing for {getCurrentNetwork().name}:</h2>
            <p>Please check network configuration in the code.</p>
            <p>Current network: <strong>{getCurrentNetwork().name}</strong></p>
            <p>Expected contract address: <code>{getCurrentNetwork().contractAddress || 'Not configured'}</code></p>
          </div>
        </div>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div className="app">
        <div className="connect-wallet">
          <div className="cyberpunk-logo">
            <h1>⚡ CYBORG RESURRECTION ⚡</h1>
            <div className="subtitle">Underground Code-Reclaimer Initiative</div>
          </div>
          <div className="mission-brief">
            <p>🤖 <strong>MISSION:</strong> Resurrect the fragmented cyborg head</p>
            <p>🔓 <strong>OBJECTIVE:</strong> Decipher the final code sequence (1-100)</p>
            <p>⚔️ <strong>METHOD:</strong> Challenge other reclaimers or the AI system</p>
          </div>
          <div className="connect-info">
            <p>📍 Neural Network: <strong>{getCurrentNetwork().name}</strong></p>
            <p>💰 Stake your {getCurrentCurrency()} to join the resistance</p>
            <p>🔍 Track operations on <a href={getCurrentNetwork().blockExplorerUrls[0]} target="_blank" rel="noopener noreferrer">Block Explorer</a></p>
          </div>
          <button onClick={() => connectWallet(true)} disabled={loading} className="btn btn-primary btn-large">
            {loading ? 'Connecting Wallet...' : 'Connect Wallet'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="header">
        <div className="cyberpunk-header">
          <h1>⚡ CYBORG RESURRECTION ⚡</h1>
          <div className="header-subtitle">Code-Reclaimer Terminal</div>
        </div>
        <div className="network-selector">
          <div className="network-label">Neural Network:</div>
          <div className="network-options">
            {Object.keys(NETWORKS).map((networkKey) => (
              <button
                key={networkKey}
                onClick={() => switchNetwork(networkKey)}
                className={`network-btn ${selectedNetwork === networkKey ? 'active' : ''}`}
                disabled={loading}
              >
                {loading && selectedNetwork !== networkKey ? '⏳' : NETWORKS[networkKey].icon} {NETWORKS[networkKey].name}
              </button>
            ))}
          </div>
          {loading && <div className="network-switching">Switching networks...</div>}
        </div>
        <div className="user-info">
          <div className="balance">Credits: {parseFloat(balance).toFixed(4)} {getCurrentCurrency()}</div>
          <div className="address">Agent #{account.slice(0, 6)}...{account.slice(-4)}</div>
          <button onClick={disconnectWallet} className="btn btn-secondary btn-small">
            Disconnect
          </button>
        </div>
      </header>

      <nav className="nav">
        <button
          onClick={() => setCurrentView('instructions')}
          className={currentView === 'instructions' ? 'nav-btn active' : 'nav-btn'}
        >
          📋 Instructions
        </button>
        <button
          onClick={() => setCurrentView('dashboard')}
          className={currentView === 'dashboard' ? 'nav-btn active' : 'nav-btn'}
        >
          🖥️ Operations
        </button>
        <button
          onClick={() => setCurrentView('matchmaking')}
          className={currentView === 'matchmaking' ? 'nav-btn active' : 'nav-btn'}
        >
          🎯 Find Opponents
        </button>
        <button
          onClick={() => setCurrentView('books')}
          className={currentView === 'books' ? 'nav-btn active' : 'nav-btn'}
        >
          📚 Chronicles
        </button>
        <button
          onClick={() => {
            console.log('🔄 Force refreshing all data...');
            setLoadingStates(prev => ({...prev, playerGames: true, availableGames: true}));
            loadPlayerGames();
            loadAvailableGames();
          }}
          disabled={loading || loadingStates.playerGames || loadingStates.availableGames}
          className="nav-btn refresh-btn"
        >
          {(loading || loadingStates.playerGames || loadingStates.availableGames) ? 'Scanning...' : '🔄 Force Sync'}
        </button>
        {isOwner && (
          <button
            onClick={withdrawFees}
            disabled={loading}
            className="nav-btn owner-btn"
            title={`Contract Balance: ${contractBalance} ${getCurrentCurrency()}`}
          >
            {loading ? 'Processing...' : `💰 Extract Fees (${parseFloat(contractBalance).toFixed(4)} ${getCurrentCurrency()})`}
          </button>
        )}
      </nav>

      <main className="main">
        {currentView === 'instructions' && (
          <div className="instructions-section">
            <div className="instructions-header">
              <h2>📋 Mission Briefing & Instructions</h2>
            </div>

            <div className="instructions-content">
              <div className="video-tutorial-section">
                <h3>🎥 Video Tutorial</h3>
                <div className="tutorial-box">
                  <p>New to the platform? Watch our complete video guide to learn how to navigate the Code-Reclaimer Terminal:</p>
                  <a
                    href="https://www.youtube.com/watch?v=YCHXmp7ETEY"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary btn-video"
                  >
                    📺 Watch Tutorial on YouTube
                  </a>
                </div>
              </div>

              <div className="briefing-section">
                <h3>🎯 Mission Overview</h3>
                <p>Welcome, Code-Reclaimer. You are part of an elite underground network tasked with resurrecting a powerful cyborg head that contains the knowledge to end the oppressive regime.</p>

                <h3>🔥 What You Can Win</h3>
                <div className="rewards-highlight">
                  <div className="reward-item">
                    <span className="reward-icon">💰</span>
                    <div className="reward-text">
                      <strong>{getCurrentCurrency()} Tokens</strong>
                      <p>Win your opponent's entire stake! Risk {selectedNetwork === 'polygon' ? '0.01 to 100' : '0.001 to 1'} {getCurrentCurrency()} per mission and claim the full pot when you decode closest to the cyborg's neural target.</p>
                    </div>
                  </div>
                  <div className="reward-item">
                    <span className="reward-icon">🏆</span>
                    <div className="reward-text">
                      <strong>Agent Supremacy</strong>
                      <p>Establish dominance in the underground network. Your victories are recorded on the blockchain forever, proving your superior decryption skills.</p>
                    </div>
                  </div>
                  <div className="reward-item">
                    <span className="reward-icon">🧬</span>
                    <div className="reward-text">
                      <strong>Cyborg Fragments</strong>
                      <p>Each successful mission recovers crucial neural data. Accumulate enough victories to piece together the ultimate weapon against the regime!</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="gameplay-section">
                <h3>⚡ How to Play</h3>
                <div className="step-list">
                  <div className="step">
                    <span className="step-number">1</span>
                    <div className="step-content">
                      <h4>Connect Neural Interface</h4>
                      <p>Link your MetaMask wallet to the {getCurrentNetwork().name} and load {getCurrentCurrency()} credits.</p>
                    </div>
                  </div>
                  <div className="step">
                    <span className="step-number">2</span>
                    <div className="step-content">
                      <h4>Choose Your Mission Type</h4>
                      <ul>
                        <li><strong>🤖 vs AI System:</strong> Battle the regime's defense system directly</li>
                        <li><strong>👥 Agent vs Agent:</strong> Challenge other code-reclaimers</li>
                        <li><strong>🎯 Matchmaking Pool:</strong> Join available missions from other agents</li>
                      </ul>
                    </div>
                  </div>
                  <div className="step">
                    <span className="step-number">3</span>
                    <div className="step-content">
                      <h4>Set Your Stakes</h4>
                      <p>Risk between {selectedNetwork === 'polygon' ? '0.01 and 100' : '0.001 and 1'} {getCurrentCurrency()}. Higher stakes mean bigger rewards but greater risk.</p>
                    </div>
                  </div>
                  <div className="step">
                    <span className="step-number">4</span>
                    <div className="step-content">
                      <h4>Input Code Sequence</h4>
                      <p>Submit your encrypted guess (1-100) with a secret nonce key. The cyborg's neural pathways generate a random target number.</p>
                    </div>
                  </div>
                  <div className="step">
                    <span className="step-number">5</span>
                    <div className="step-content">
                      <h4>Decrypt & Win</h4>
                      <p>Reveal your guess and nonce. The agent closest to the cyborg's target wins the entire pot!</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="strategy-section">
                <h3>🧠 Winning Strategy</h3>
                <div className="strategy-tips">
                  <div className="tip">
                    <span className="tip-icon">🎲</span>
                    <p><strong>Random is Key:</strong> The cyborg uses blockchain randomness. There's no pattern to exploit—trust your instincts.</p>
                  </div>
                  <div className="tip">
                    <span className="tip-icon">🔐</span>
                    <p><strong>Secure Your Nonce:</strong> Remember your secret key! You'll need it to decrypt your code sequence.</p>
                  </div>
                  <div className="tip">
                    <span className="tip-icon">⏰</span>
                    <p><strong>Act Fast:</strong> Missions have a 1-hour time limit. Delayed responses result in forfeit.</p>
                  </div>
                  <div className="tip">
                    <span className="tip-icon">💡</span>
                    <p><strong>Start Small:</strong> New agents should begin with smaller stakes while learning the system.</p>
                  </div>
                </div>
              </div>

              <div className="technical-section">
                <h3>🔧 Technical Details</h3>
                <ul className="tech-list">
                  <li><strong>Platform Fee:</strong> 2% of the pot goes to network maintenance</li>
                  <li><strong>Commit-Reveal:</strong> Fair play guaranteed through cryptographic hashing</li>
                  <li><strong>Blockchain Security:</strong> All missions recorded on {getCurrentNetwork().name}</li>
                  <li><strong>Smart Contract:</strong> Fully audited and transparent code execution</li>
                </ul>
              </div>

              <div className="chronicles-promo">
                <div className="promo-box">
                  <h3>📚 Dive Deeper Into the Resistance</h3>
                  <p>The cyborg uprising didn't happen overnight. Discover the hidden history, the key players, and the technological breakthroughs that led to this moment.</p>
                  <p><strong>Classified documents</strong> reveal the true scope of the resistance movement and the power contained within the fragmented cyborg consciousness.</p>
                  <button
                    onClick={() => setCurrentView('books')}
                    className="btn btn-primary chronicles-btn"
                  >
                    🔍 Access Chronicles Database →
                  </button>
                  <div className="classified-stamp">CLASSIFIED - LEVEL 7 CLEARANCE</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {currentView === 'dashboard' && (
          <div className="dashboard">
            <div className="dashboard-header">
              <h2>🔬 Active Resurrection Attempts ({games.length})</h2>

              {/* Pending invitations detector */}
              {(() => {
                const pendingInvites = games.filter(game =>
                  game.state === 0 &&
                  !game.isVsContract &&
                  game.player2.toLowerCase() === account.toLowerCase() &&
                  game.player1.toLowerCase() !== account.toLowerCase()
                );

                if (pendingInvites.length > 0) {
                  return (
                    <div className="pending-invites-alert">
                      <h3>📨 You have {pendingInvites.length} pending challenge{pendingInvites.length > 1 ? 's' : ''}!</h3>
                      <div className="invites-list">
                        {pendingInvites.map(game => (
                          <div key={game.id} className="invite-item">
                            <span>Mission #{game.id} from {game.player1.slice(0, 6)}...{game.player1.slice(-4)} ({game.betAmount} ${getCurrentCurrency()})</span>
                            <button
                              onClick={() => joinGame(game.id, game.betAmount)}
                              className="btn btn-primary btn-small"
                            >
                              Accept Challenge
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
              <div className="dashboard-controls">
                <div className="auto-refresh-controls">
                  <div className="auto-refresh-indicator">
                    <span className={`pulse-dot ${liveUpdatesEnabled ? '' : 'disabled'}`}></span>
                    <small>{liveUpdatesEnabled ? 'Live updates enabled' : 'Live updates disabled'}</small>
                  </div>
                  <button
                    onClick={() => setLiveUpdatesEnabled(!liveUpdatesEnabled)}
                    className={`btn btn-sm ${liveUpdatesEnabled ? 'btn-secondary' : 'btn-primary'}`}
                    title={liveUpdatesEnabled ? 'Disable live updates' : 'Enable live updates'}
                  >
                    {liveUpdatesEnabled ? '⏸️ Pause' : '▶️ Resume'}
                  </button>
                </div>
                {games.filter(game => !hiddenGames.includes(game.id)).length > 0 && (
                  <div className="clear-operations">
                    <button
                      onClick={() => {
                        setGames([]);
                        setGameGuesses({});
                        setGameNonces({});
                        alert('🧹 Operations cleared temporarily! Use Force Sync to reload active missions.');
                      }}
                      className="btn btn-secondary btn-small"
                      title="Clear all missions from view temporarily"
                    >
                      🧹 Clear (Temp)
                    </button>

                    {/* Show permanent hide option only if there are finished games */}
                    {games.filter(game => !hiddenGames.includes(game.id) && (parseInt(game.state) === 3 || parseInt(game.state) === 4)).length > 0 && (
                      <button
                        onClick={() => {
                          const finishedGames = games.filter(game =>
                            !hiddenGames.includes(game.id) &&
                            (parseInt(game.state) === 3 || parseInt(game.state) === 4)
                          );
                          const finishedGameIds = finishedGames.map(game => game.id);

                          if (finishedGameIds.length > 0) {
                            // eslint-disable-next-line no-restricted-globals
                            if (confirm(`🗑️ Permanently hide ${finishedGameIds.length} finished mission(s)? This cannot be undone.`)) {
                              hideGamesPermanently(finishedGameIds);
                              alert(`✅ ${finishedGameIds.length} finished mission(s) permanently hidden.`);
                            }
                          }
                        }}
                        className="btn btn-danger btn-small"
                        title="Permanently hide finished missions"
                      >
                        🗑️ Hide Finished
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
            {loadingStates.playerGames ? (
              <div className="loading-container">
                <div className="cyborg-head">🔄</div>
                <p>Accessing your neural command center...</p>
              </div>
            ) : games.length === 0 ? (
              <div className="no-games">
                <div className="cyborg-head">🤖</div>
                <p>No active operations detected.</p>
                <p>The cyborg head awaits your first code-breaking attempt...</p>
                <button
                  onClick={() => setCurrentView('create')}
                  className="btn btn-primary"
                >
                  ⚡ Begin First Mission
                </button>
              </div>
            ) : (
              <div className="games-grid">
                {games
                  .filter(game => !hiddenGames.includes(game.id)) // Filter out permanently hidden games
                  .sort((a, b) => parseInt(b.id) - parseInt(a.id)) // Sort by ID descending (most recent first)
                  .map((game) => (
                  <div key={game.id} className="game-card">
                    <div className="game-header">
                      <h3>⚡ Mission #{game.id}</h3>
                      <div className="status-container">
                        <span className={`status status-${game.state}`}>
                          {getGameStateText(game.state)}
                        </span>
                        <span className="phase-indicator">
                          Phase {getPhaseProgress(game.state)}
                        </span>
                      </div>
                    </div>

                    <div className="game-info">
                      <p><strong>Stakes:</strong> {game.betAmount} {getCurrentCurrency()}</p>
                      <p><strong>Target:</strong> {game.isVsContract ? 'AI System 🤖' : 'Rival Reclaimer 👥'}</p>
                      <p><strong>Format:</strong> Single Shot Winner-Takes-All</p>
                      {game.currentRound > 0 && (
                        <p><strong>Progress:</strong> Single shot game in progress</p>
                      )}

                      {/* Show saved code sequence and key if available */}
                      {(gameGuesses[game.id] || gameNonces[game.id]) && (game.state === 1 || game.state === 2) && (
                        <div className="saved-codes">
                          <p><strong>💾 Your Saved Values:</strong></p>
                          {gameGuesses[game.id] && (
                            <p className="code-display"><strong>Code Sequence:</strong> <span className="code-value">{gameGuesses[game.id]}</span></p>
                          )}
                          {gameNonces[game.id] && (
                            <p className="code-display"><strong>Encryption Key:</strong> <span className="code-value">{gameNonces[game.id]}</span></p>
                          )}
                          {(!gameGuesses[game.id] || !gameNonces[game.id]) && (
                            <p className="code-missing"><strong>⚠️ Missing:</strong> {!gameGuesses[game.id] ? 'Code Sequence ' : ''}{!gameNonces[game.id] ? 'Encryption Key' : ''}</p>
                          )}
                          <small className="code-help">🔐 These values are auto-saved for the decryption phase</small>
                        </div>
                      )}

                      {/* Debug info for troubleshooting */}
                      {(game.state === 1 || game.state === 2) && (
                        <details className="debug-section">
                          <summary>🔧 Debug Info (Click to expand)</summary>
                          <div className="debug-content">
                            <p><strong>Game State:</strong> {game.state} ({getGameStateText(game.state)})</p>
                            <p><strong>Game Status:</strong> In Progress</p>
                            <p><strong>Has Guess:</strong> {gameGuesses[game.id] ? '✅ Yes' : '❌ No'}</p>
                            <p><strong>Has Nonce:</strong> {gameNonces[game.id] ? '✅ Yes' : '❌ No'}</p>
                            <p><strong>LocalStorage Status:</strong> {(() => {
                              try {
                                const saved = localStorage.getItem('cyborg_game_guesses');
                                return saved ? '✅ Available' : '❌ Empty';
                              } catch { return '❌ Error'; }
                            })()}</p>
                          </div>
                        </details>
                      )}

                      <p><strong>Agent 1:</strong> {game.player1.slice(0, 6)}...{game.player1.slice(-4)}</p>
                      <p><strong>Agent 2:</strong> {
                        game.player2 === ethers.constants.AddressZero
                          ? 'Seeking partner...'
                          : game.isVsContract
                          ? 'AI System 🤖'
                          : `${game.player2.slice(0, 6)}...${game.player2.slice(-4)}`
                      }</p>
                      {game.roundInfo && game.roundInfo.completed && game.state === 3 && (
                        <p><strong>Final Code:</strong> {game.roundInfo.targetNumber}</p>
                      )}
                      {game.gameWinner && game.gameWinner !== ethers.constants.AddressZero && (
                        <p><strong>Successful Agent:</strong> {
                          game.gameWinner.toLowerCase() === getCurrentContractAddress().toLowerCase()
                            ? 'AI System 🤖'
                            : `${game.gameWinner.slice(0, 6)}...${game.gameWinner.slice(-4)}`
                        }</p>
                      )}
                    </div>

                    <div className="game-actions">
                      {getGameActions(game)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}


        {currentView === 'create-multiplayer' && (
          <div className="create-multiplayer">
            <div className="multiplayer-header">
              <h2>⚡ Post Recruitment Mission</h2>
              <div className="auto-refresh-controls">
                <div className="auto-refresh-indicator">
                  <span className={`pulse-dot ${liveUpdatesEnabled ? '' : 'disabled'}`}></span>
                  <small>{liveUpdatesEnabled ? 'Live updates enabled' : 'Live updates disabled'}</small>
                </div>
                <button
                  onClick={() => setLiveUpdatesEnabled(!liveUpdatesEnabled)}
                  className={`btn btn-sm ${liveUpdatesEnabled ? 'btn-secondary' : 'btn-primary'}`}
                  title={liveUpdatesEnabled ? 'Disable live updates' : 'Enable live updates'}
                >
                  {liveUpdatesEnabled ? '⏸️ Pause' : '▶️ Resume'}
                </button>
              </div>
            </div>

            <div className="mission-briefing">
              <div className="mission-icon">🎯</div>
              <p>Deploy an open recruitment mission to the agent network. Other code-reclaimers can discover and join your mission through the matchmaking pool.</p>
            </div>

            <div className="mission-config">
              <div className="config-section">
                <h3>📊 Mission Parameters</h3>

                <div className="form-group">
                  <label>💰 Stakes ({getCurrentCurrency()})</label>
                  <select
                    value={betAmount}
                    onChange={(e) => setBetAmount(e.target.value)}
                    className="form-control"
                  >
                    {selectedNetwork === 'polygon' ? (
                      <>
                        <option value="0.01">0.01 {getCurrentCurrency()} (Minimum Risk)</option>
                        <option value="1">1 {getCurrentCurrency()} (Low Risk)</option>
                        <option value="5">5 {getCurrentCurrency()} (Medium Risk)</option>
                        <option value="10">10 {getCurrentCurrency()} (High Risk)</option>
                        <option value="25">25 {getCurrentCurrency()} (Very High Risk)</option>
                        <option value="50">50 {getCurrentCurrency()} (Extreme Risk)</option>
                        <option value="75">75 {getCurrentCurrency()} (Ultra Risk)</option>
                        <option value="100">100 {getCurrentCurrency()} (Maximum Risk)</option>
                      </>
                    ) : (
                      <>
                        <option value="0.001">0.001 {getCurrentCurrency()} (Minimum Risk)</option>
                        <option value="0.01">0.01 {getCurrentCurrency()} (Low Risk)</option>
                        <option value="0.1">0.1 {getCurrentCurrency()} (Medium Risk)</option>
                        <option value="0.5">0.5 {getCurrentCurrency()} (High Risk)</option>
                        <option value="1">1 {getCurrentCurrency()} (Maximum Risk)</option>
                      </>
                    )}
                  </select>
                </div>

                <div className="form-group">
                  <label>🎲 Mission Format</label>
                  <div className="format-display">
                    <p><strong>Single Shot - Winner Takes All</strong></p>
                    <small>Every game is a decisive single-round battle. No second chances!</small>
                  </div>
                </div>
              </div>

              <div className="config-section">
                <h3>🌐 Recruitment Details</h3>
                <div className="recruitment-info">
                  <div className="info-item">
                    <span className="info-icon">🔍</span>
                    <div>
                      <strong>Open Recruitment:</strong>
                      <p>Your mission will appear in the agent pool for anyone to join</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-icon">⏱️</span>
                    <div>
                      <strong>1 Hour Deadline:</strong>
                      <p>Mission expires if no agent joins within 1 hour</p>
                    </div>
                  </div>
                  <div className="info-item">
                    <span className="info-icon">💎</span>
                    <div>
                      <strong>Stakes Matched:</strong>
                      <p>Joining agents must match your exact stake amount</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mission-actions">
              <button
                onClick={createMultiplayerMission}
                disabled={loading}
                className="btn btn-primary btn-large"
              >
                {loading ? 'Deploying Mission...' : `⚡ Deploy Mission (${betAmount} ${getCurrentCurrency()})`}
              </button>

              <button
                onClick={() => setCurrentView('matchmaking')}
                className="btn btn-secondary"
              >
                ← Back to Agent Pool
              </button>
            </div>
          </div>
        )}

        {currentView === 'matchmaking' && (
          <div className="matchmaking">
            <div className="dashboard-header">
              <h2>🎯 Agent Recruitment Pool ({availableGames.length})</h2>
              <div className="auto-refresh-controls">
                <div className="auto-refresh-indicator">
                  <span className={`pulse-dot ${liveUpdatesEnabled ? '' : 'disabled'}`}></span>
                  <small>{liveUpdatesEnabled ? 'Live updates enabled' : 'Live updates disabled'}</small>
                </div>
                <button
                  onClick={() => setLiveUpdatesEnabled(!liveUpdatesEnabled)}
                  className={`btn btn-sm ${liveUpdatesEnabled ? 'btn-secondary' : 'btn-primary'}`}
                  title={liveUpdatesEnabled ? 'Disable live updates' : 'Enable live updates'}
                >
                  {liveUpdatesEnabled ? '⏸️ Pause' : '▶️ Resume'}
                </button>
              </div>
            </div>
            <div className="matchmaking-intro">
              <p>🤖 Find other code-reclaimers seeking partners for cyborg resurrection missions.</p>
              <p>⚡ Accept their challenge or create your own mission in the pool.</p>
            </div>

            {loadingStates.availableGames ? (
              <div className="loading-container">
                <div className="cyborg-head">🔄</div>
                <p>Scanning neural network for available missions...</p>
              </div>
            ) : availableGames.length === 0 ? (
              <div className="no-games">
                <div className="cyborg-head">🔍</div>
                <p>No active recruitment detected in the network.</p>
                <p>Create an open mission to start recruiting agents...</p>
                <button
                  onClick={() => setCurrentView('create-multiplayer')}
                  className="btn btn-primary"
                >
                  ⚡ Post Recruitment Mission
                </button>
              </div>
            ) : (
              <div className="games-grid">
                {availableGames.map((game) => (
                  <div key={game.id} className="game-card matchmaking-card">
                    <div className="game-header">
                      <h3>⚡ Open Mission #{game.id}</h3>
                      <span className="status status-0">Seeking Partner</span>
                    </div>

                    <div className="game-info">
                      <p><strong>Mission Creator:</strong> Agent #{game.player1.slice(0, 6)}...{game.player1.slice(-4)}</p>
                      <p><strong>Stakes:</strong> {game.betAmount} {getCurrentCurrency()}</p>
                      <p><strong>Format:</strong> Single Shot Winner-Takes-All</p>
                      <p><strong>Posted:</strong> {game.createdAt.toLocaleString()}</p>
                      <p><strong>Expires:</strong> {game.deadline.toLocaleString()}</p>
                    </div>

                    <div className="game-actions">
                      <button
                        onClick={() => joinGame(game.id, game.betAmount)}
                        disabled={loading}
                        className="btn btn-primary"
                      >
                        {loading ? 'Joining Mission...' : `Accept Challenge (${game.betAmount} ${getCurrentCurrency()})`}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="matchmaking-actions">
              <button
                onClick={() => {
                  loadAvailableGames();
                }}
                disabled={loading}
                className="btn btn-secondary"
              >
                {loading ? 'Scanning...' : '🔄 Refresh Pool'}
              </button>
              <button
                onClick={() => setCurrentView('create-multiplayer')}
                className="btn btn-primary"
              >
                ⚡ Post Recruitment Mission
              </button>
            </div>
          </div>
        )}

        {currentView === 'books' && (
          <div className="books-section">
            <div className="books-header">
              <h2>📚 Cyborg Chronicles</h2>
              <div className="auto-refresh-controls">
                <div className="auto-refresh-indicator">
                  <span className={`pulse-dot ${liveUpdatesEnabled ? '' : 'disabled'}`}></span>
                  <small>{liveUpdatesEnabled ? 'Live updates enabled' : 'Live updates disabled'}</small>
                </div>
                <button
                  onClick={() => setLiveUpdatesEnabled(!liveUpdatesEnabled)}
                  className={`btn btn-sm ${liveUpdatesEnabled ? 'btn-secondary' : 'btn-primary'}`}
                  title={liveUpdatesEnabled ? 'Disable live updates' : 'Enable live updates'}
                >
                  {liveUpdatesEnabled ? '⏸️ Pause' : '▶️ Resume'}
                </button>
              </div>
            </div>
            <div className="archive-intro">
              <p>Discover the origins of the cyborg uprising and the resistance movement through these classified documents.</p>
            </div>
            <div className="books-grid">
              <div className="book-card">
                <div className="book-cover">📖</div>
                <h3>Cyborgs Almighty</h3>
                <p>The founding text of the cybernetic revolution. Explore the philosophical and technological foundations that led to the cyborg uprising.</p>
                <a href="https://www.amazon.ca/Cyborgs-Almighty-Roy-Anthony/dp/B0CDNJ65PW" target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                  Access Chronicles
                </a>
              </div>

              <div className="book-card">
                <div className="book-cover">⚔️</div>
                <h3>De Anima: The Great Schism</h3>
                <p>The story of the great divide between human and machine consciousness. Understand the conflict that shaped our world.</p>
                <a href="https://www.amazon.com/Anima-Great-Schism-Roy-Anthony/dp/1980530564" target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                  Access Chronicles
                </a>
              </div>

              <div className="book-card">
                <div className="book-cover">🔧</div>
                <h3>De Anima: Calibration</h3>
                <p>The technical manual for understanding the calibration process that connects human and machine minds.</p>
                <a href="https://www.amazon.com/-/es/Anima-Calibration-Roy-Anthony/dp/1089174357" target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                  Access Chronicles
                </a>
              </div>

              <div className="book-card">
                <div className="book-cover">🎭</div>
                <h3>The Nihilist Party</h3>
                <p>A dark exploration of the philosophical movement that emerged from the ashes of the old world order.</p>
                <a href="https://www.amazon.com/Nihilist-Party-Roy-Anthony-ebook/dp/B0893YPRD2/ref=sr_1_1?crid=3K3JOBWKNFX22&dib=eyJ2IjoiMSJ9.vyUTSLJIZ65DpsWaBHfdZQ.7pDCK--vCfDrJta5PkLxJlFnJl_RSB3paeJtlhBkyEA&dib_tag=se&keywords=the+nihilist+party&qid=1754084243&s=digital-text&sprefix=the+nihilist+party%2Cdigital-text%2C251&sr=1-1" target="_blank" rel="noopener noreferrer" className="btn btn-primary">
                  Access Chronicles
                </a>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// Updated CSS Styles with Cyberpunk Theme
const styles = `
  .app {
    min-height: 100vh;
    background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
    font-family: 'Courier New', 'Segoe UI', monospace;
    color: #00ff88;
  }

  .cyberpunk-logo h1 {
    font-size: 2.5rem;
    text-shadow: 0 0 20px #00ff88, 0 0 40px #00ff88;
    margin-bottom: 0.5rem;
    letter-spacing: 2px;
  }

  .subtitle {
    color: #ff6b6b;
    font-size: 1.2rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 2rem;
  }

  .mission-brief {
    background: rgba(0, 255, 136, 0.1);
    border: 1px solid #00ff88;
    border-radius: 0.5rem;
    padding: 1.5rem;
    margin: 2rem 0;
    backdrop-filter: blur(10px);
  }

  .mission-brief p {
    margin: 0.75rem 0;
    font-size: 1rem;
  }

  .cyberpunk-header h1 {
    font-size: 1.8rem;
    text-shadow: 0 0 10px #00ff88;
    margin: 0;
  }

  .header-subtitle {
    color: #ff6b6b;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .cyborg-head {
    font-size: 4rem;
    animation: pulse 2s infinite;
    margin-bottom: 1rem;
  }

  @keyframes pulse {
    0% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(1.05); }
    100% { opacity: 1; transform: scale(1); }
  }


  .first-game-options {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;
  }

  .no-games-actions {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;
    margin-top: 1rem;
  }

  .matchmaking-create-options {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;
  }

  .books-section {
    color: white;
  }

  .archive-intro {
    text-align: center;
    background: rgba(0, 255, 136, 0.1);
    border: 1px solid #00ff88;
    border-radius: 0.5rem;
    padding: 1.5rem;
    margin-bottom: 2rem;
  }

  .books-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    margin-top: 2rem;
  }

  .book-card {
    background: rgba(255, 107, 107, 0.1);
    border: 1px solid #ff6b6b;
    border-radius: 1rem;
    padding: 2rem;
    text-align: center;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
  }

  .book-card:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(255, 107, 107, 0.3);
    border-color: #00ff88;
  }

  .book-cover {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  .book-card h3 {
    color: #ff6b6b;
    margin-bottom: 1rem;
    text-shadow: 0 0 5px #ff6b6b;
  }

  .book-card p {
    color: rgba(255, 255, 255, 0.8);
    margin-bottom: 1.5rem;
    line-height: 1.5;
  }

  .matchmaking {
    color: white;
  }

  .matchmaking-intro {
    text-align: center;
    background: rgba(0, 255, 136, 0.1);
    border: 1px solid #00ff88;
    border-radius: 0.5rem;
    padding: 1.5rem;
    margin-bottom: 2rem;
  }

  .matchmaking-intro p {
    margin: 0.5rem 0;
  }

  .matchmaking-card {
    border-color: rgba(255, 107, 107, 0.4);
    background: rgba(255, 107, 107, 0.05);
  }

  .matchmaking-card:hover {
    border-color: #ff6b6b;
    box-shadow: 0 0 20px rgba(255, 107, 107, 0.3);
  }

  .matchmaking-actions {
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-top: 2rem;
    padding-top: 2rem;
    border-top: 1px solid rgba(0, 255, 136, 0.2);
  }

  .dashboard-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
  }

  .auto-refresh-indicator {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: rgba(0, 255, 136, 0.8);
    font-size: 0.8rem;
  }

  .pulse-dot {
    width: 8px;
    height: 8px;
    background: #00ff88;
    border-radius: 50%;
    animation: pulse-dot 2s infinite;
  }

  @keyframes pulse-dot {
    0% { opacity: 0.5; transform: scale(1); }
    50% { opacity: 1; transform: scale(1.2); }
    100% { opacity: 0.5; transform: scale(1); }
  }

  .dashboard-controls {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .clear-btn {
    background: rgba(255, 107, 107, 0.2) !important;
    border-color: rgba(255, 107, 107, 0.4) !important;
    color: #ff6b6b !important;
    font-size: 0.8rem !important;
    padding: 0.4rem 0.8rem !important;
  }

  .clear-btn:hover {
    background: rgba(255, 107, 107, 0.3) !important;
    border-color: rgba(255, 107, 107, 0.6) !important;
  }

  .submission-status {
    background: rgba(0, 255, 136, 0.1);
    border: 1px solid rgba(0, 255, 136, 0.3);
    border-radius: 0.5rem;
    padding: 1rem;
    margin-top: 1rem;
  }

  .submission-status p {
    margin: 0.5rem 0;
    color: rgba(255, 255, 255, 0.9);
  }

  .loading-container {
    text-align: center;
    padding: 3rem;
    background: rgba(0, 255, 136, 0.05);
    border: 1px solid rgba(0, 255, 136, 0.2);
    border-radius: 1rem;
  }

  .loading-container .cyborg-head {
    font-size: 3rem;
    animation: spin 2s linear infinite;
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .saved-codes {
    background: rgba(0, 255, 136, 0.1);
    border: 1px solid rgba(0, 255, 136, 0.3);
    border-radius: 0.5rem;
    padding: 1rem;
    margin: 1rem 0;
  }

  .code-display {
    margin: 0.5rem 0;
    font-size: 0.9rem;
  }

  .code-value {
    background: rgba(0, 0, 0, 0.3);
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    font-family: monospace;
    color: #00ff88;
    font-weight: bold;
  }

  .code-help {
    color: rgba(0, 255, 136, 0.8);
    font-style: italic;
  }

  .code-missing {
    margin: 0.5rem 0;
    font-size: 0.9rem;
    color: #f59e0b;
  }

  .debug-section {
    margin: 1rem 0;
    background: rgba(0, 0, 0, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 0.5rem;
    padding: 0.5rem;
  }

  .debug-section summary {
    cursor: pointer;
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.8rem;
    padding: 0.5rem;
  }

  .debug-section summary:hover {
    color: rgba(255, 255, 255, 0.9);
  }

  .debug-content {
    padding: 0.5rem;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.6);
    font-family: monospace;
  }

  .debug-content p {
    margin: 0.25rem 0;
  }

  .waiting-reveal-section {
    text-align: center;
    padding: 1.5rem;
    background: rgba(16, 185, 129, 0.1);
    border: 1px solid rgba(16, 185, 129, 0.3);
    border-radius: 0.75rem;
    margin: 1rem 0;
  }

  .waiting-opponent {
    margin-top: 1rem;
  }

  .opponent-status h4 {
    color: #10b981;
    margin-bottom: 1rem;
    font-size: 1.1rem;
  }

  .opponent-status p {
    margin: 0.5rem 0;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.95rem;
  }

  .reveal-details {
    background: rgba(0, 0, 0, 0.2);
    padding: 1rem;
    border-radius: 0.5rem;
    margin: 1rem 0;
    border: 1px solid rgba(16, 185, 129, 0.2);
  }

  .auto-refresh-waiting {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin: 1rem 0;
    color: rgba(16, 185, 129, 0.8);
    font-style: italic;
  }

  .timeout-info {
    background: rgba(245, 158, 11, 0.1);
    border: 1px solid rgba(245, 158, 11, 0.3);
    border-radius: 0.5rem;
    padding: 0.75rem;
    margin-top: 1rem;
  }

  .timeout-info p {
    margin: 0.25rem 0;
    color: rgba(245, 158, 11, 0.9);
  }

  .reveal-status {
    background: rgba(59, 130, 246, 0.1);
    border: 1px solid rgba(59, 130, 246, 0.3);
    border-radius: 0.5rem;
    padding: 1rem;
    margin: 1rem 0;
  }

  .reveal-status h4 {
    color: #3b82f6;
    margin: 0 0 0.75rem 0;
    text-align: center;
    font-size: 1rem;
  }

  .status-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
    margin-bottom: 0.75rem;
  }

  .status-item {
    text-align: center;
    padding: 0.5rem;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 0.375rem;
  }

  .status-label {
    display: block;
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.7);
    margin-bottom: 0.25rem;
  }

  .status-value {
    display: block;
    font-weight: bold;
    font-size: 0.9rem;
  }

  .status-value.completed {
    color: #10b981;
  }

  .status-value.pending {
    color: #f59e0b;
  }

  .waiting-note {
    text-align: center;
    font-style: italic;
    color: rgba(245, 158, 11, 0.9);
    margin: 0.5rem 0 0 0;
    font-size: 0.9rem;
  }

  .step.pending .step-number {
    background: rgba(245, 158, 11, 0.3);
    color: #f59e0b;
  }

  .step.pending .step-text {
    color: rgba(245, 158, 11, 0.8);
  }

  .multi-wallet-helper {
    background: rgba(59, 130, 246, 0.1);
    border: 1px solid rgba(59, 130, 246, 0.3);
    border-radius: 0.75rem;
    padding: 1.5rem;
    margin: 1.5rem 0;
    text-align: left;
  }

  .multi-wallet-helper h4 {
    color: #3b82f6;
    margin: 0 0 1rem 0;
    text-align: center;
    font-size: 1.1rem;
  }

  .wallet-info {
    background: rgba(0, 0, 0, 0.2);
    padding: 0.75rem;
    border-radius: 0.5rem;
    margin: 0.75rem 0;
    font-family: monospace;
    font-size: 0.85rem;
  }

  .wallet-info p {
    margin: 0.25rem 0;
    color: rgba(255, 255, 255, 0.9);
  }

  .coordination-help {
    margin-top: 1rem;
  }

  .coordination-help p {
    margin: 0.5rem 0;
    color: rgba(255, 255, 255, 0.9);
  }

  .coordination-help ol {
    margin: 0.75rem 0;
    padding-left: 1.5rem;
    color: rgba(255, 255, 255, 0.8);
  }

  .coordination-help li {
    margin: 0.5rem 0;
    line-height: 1.4;
  }

  .quick-actions {
    display: flex;
    gap: 0.75rem;
    margin: 1rem 0;
    justify-content: center;
    flex-wrap: wrap;
  }

  .quick-actions .btn {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
  }

  .auto-switch-reminder {
    text-align: center;
    margin-top: 1rem;
    padding-top: 0.75rem;
    border-top: 1px solid rgba(59, 130, 246, 0.2);
  }

  .auto-switch-reminder p {
    margin: 0;
    color: rgba(245, 158, 11, 0.9);
  }

  .multiplayer-info {
    background: rgba(16, 185, 129, 0.1);
    border: 1px solid rgba(16, 185, 129, 0.3);
    border-radius: 0.5rem;
    padding: 1rem;
    margin-bottom: 1rem;
  }

  .player-roles p {
    margin: 0.5rem 0;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
  }

  .coordination-reminder {
    text-align: center;
    margin-top: 1rem;
    padding: 0.75rem;
    background: rgba(245, 158, 11, 0.1);
    border: 1px solid rgba(245, 158, 11, 0.3);
    border-radius: 0.5rem;
  }

  .coordination-reminder small {
    color: rgba(245, 158, 11, 0.9);
    font-style: italic;
  }

  .direct-invitation-helper {
    background: rgba(59, 130, 246, 0.1);
    border: 1px solid rgba(59, 130, 246, 0.3);
    border-radius: 0.75rem;
    padding: 1.5rem;
    margin: 1.5rem 0;
    text-align: center;
  }

  .direct-invitation-helper h4 {
    color: #3b82f6;
    margin: 0 0 1rem 0;
    font-size: 1.1rem;
  }

  .invitation-details {
    background: rgba(0, 0, 0, 0.2);
    padding: 1rem;
    border-radius: 0.5rem;
    margin: 1rem 0;
  }

  .invitation-details p {
    margin: 0.5rem 0;
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
  }

  .invited-agent-info {
    margin-bottom: 0.75rem;
  }

  .invited-agent-info p {
    margin: 0 0 0.5rem 0;
  }

  .agent-address {
    background: rgba(0, 0, 0, 0.3);
    padding: 0.75rem;
    border-radius: 0.375rem;
    font-family: monospace;
    font-size: 0.8rem;
    color: #00ff88;
    word-break: break-all;
    line-height: 1.4;
    border: 1px solid rgba(0, 255, 136, 0.3);
    overflow-wrap: break-word;
    hyphens: auto;
  }

  .notification-warning {
    background: rgba(245, 158, 11, 0.1);
    border: 1px solid rgba(245, 158, 11, 0.3);
    border-radius: 0.5rem;
    padding: 1rem;
    margin: 1rem 0;
  }

  .notification-warning p {
    margin: 0.5rem 0;
    color: rgba(245, 158, 11, 0.9);
    font-size: 0.9rem;
  }

  .invitation-actions {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin: 1.5rem 0;
    align-items: center;
  }

  .invitation-actions .btn {
    min-width: 200px;
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
  }

  .waiting-tips {
    background: rgba(16, 185, 129, 0.1);
    border: 1px solid rgba(16, 185, 129, 0.3);
    border-radius: 0.5rem;
    padding: 1rem;
    margin: 1rem 0;
    text-align: left;
  }

  .waiting-tips h5 {
    color: #10b981;
    margin: 0 0 0.75rem 0;
    font-size: 0.95rem;
  }

  .waiting-tips ul {
    margin: 0;
    padding-left: 1.5rem;
    color: rgba(255, 255, 255, 0.8);
  }

  .waiting-tips li {
    margin: 0.5rem 0;
    font-size: 0.85rem;
    line-height: 1.4;
  }

  .pending-invites-alert {
    background: rgba(239, 68, 68, 0.1);
    border: 2px solid rgba(239, 68, 68, 0.4);
    border-radius: 0.75rem;
    padding: 1.5rem;
    margin-bottom: 2rem;
    animation: pulse-alert 2s infinite;
  }

  .pending-invites-alert h3 {
    color: #ef4444;
    margin: 0 0 1rem 0;
    text-align: center;
    font-size: 1.2rem;
  }

  .invites-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .invite-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    background: rgba(0, 0, 0, 0.2);
    padding: 0.75rem;
    border-radius: 0.5rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .invite-item span {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
  }

  @keyframes pulse-alert {
    0% { border-color: rgba(239, 68, 68, 0.4); }
    50% { border-color: rgba(239, 68, 68, 0.8); }
    100% { border-color: rgba(239, 68, 68, 0.4); }
  }

  .waiting-section {
    text-align: center;
  }

  .cancel-option {
    margin-top: 1rem;
    padding: 1rem;
    background: rgba(255, 107, 107, 0.1);
    border: 1px solid rgba(255, 107, 107, 0.3);
    border-radius: 0.5rem;
  }

  .cancel-help {
    display: block;
    color: rgba(255, 107, 107, 0.8);
    margin: 0.25rem 0;
    font-size: 0.8rem;
  }

  .phase-progress {
    text-align: center;
    margin-bottom: 1rem;
  }

  .progress-text {
    background: rgba(0, 255, 136, 0.2);
    color: #00ff88;
    padding: 0.5rem 1rem;
    border-radius: 1rem;
    font-size: 0.9rem;
    font-weight: bold;
    display: inline-block;
  }

  .progress-text.completed {
    background: rgba(16, 185, 129, 0.2);
    color: #10b981;
  }

  .status-container {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.25rem;
  }

  .phase-indicator {
    background: rgba(0, 255, 136, 0.1);
    color: rgba(0, 255, 136, 0.8);
    padding: 0.2rem 0.5rem;
    border-radius: 0.5rem;
    font-size: 0.75rem;
    border: 1px solid rgba(0, 255, 136, 0.3);
  }

  .completed-mission {
    background: rgba(16, 185, 129, 0.1);
    border: 1px solid rgba(16, 185, 129, 0.3);
    margin-bottom: 1rem;
  }

  .phase-overview {
    margin-top: 1.5rem;
    padding: 1rem;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 0.5rem;
    border: 1px solid rgba(0, 255, 136, 0.2);
  }

  .phase-overview h4 {
    color: #00ff88;
    margin: 0 0 1rem 0;
    text-align: center;
  }

  .phase-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .phase-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.5rem;
    background: rgba(0, 255, 136, 0.05);
    border-radius: 0.5rem;
  }

  .phase-num {
    width: 24px;
    height: 24px;
    background: #00ff88;
    color: #0f0f23;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 0.8rem;
    flex-shrink: 0;
  }

  .phase-desc {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
  }

  .setup-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100vh;
    text-align: center;
    color: white;
    padding: 2rem;
  }

  .setup-instructions {
    background: rgba(255, 255, 255, 0.1);
    padding: 2rem;
    border-radius: 1rem;
    max-width: 600px;
    text-align: left;
  }

  .setup-instructions ol {
    text-align: left;
    margin: 1rem 0;
  }

  .setup-instructions code {
    background: rgba(0, 0, 0, 0.3);
    padding: 0.5rem;
    border-radius: 0.25rem;
    display: block;
    margin: 0.5rem 0;
    font-family: monospace;
  }

  .connect-wallet {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100vh;
    text-align: center;
    color: white;
  }

  .connect-info {
    background: rgba(255, 255, 255, 0.1);
    padding: 1.5rem;
    border-radius: 1rem;
    margin: 2rem 0;
    max-width: 400px;
  }

  .connect-info a {
    color: #60a5fa;
    text-decoration: none;
  }

  .connect-info a:hover {
    text-decoration: underline;
  }

  .header {
    background: rgba(0, 0, 0, 0.3);
    backdrop-filter: blur(10px);
    border-bottom: 1px solid #00ff88;
    padding: 1rem 2rem;
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    color: #00ff88;
    gap: 1rem;
  }

  .cyberpunk-header {
    justify-self: start;
  }

  .network-selector {
    justify-self: center;
    display: flex;
    align-items: center;
    gap: 1rem;
    font-size: 0.9rem;
  }

  .user-info {
    justify-self: end;
    display: flex;
    gap: 1rem;
    align-items: center;
  }

  .network-label {
    color: #ff6b6b;
    font-weight: bold;
    min-width: max-content;
  }

  .network-options {
    display: flex;
    gap: 0.5rem;
  }

  .network-btn {
    padding: 0.3rem 0.8rem;
    border: 1px solid rgba(0, 255, 136, 0.3);
    background: rgba(0, 0, 0, 0.3);
    color: #ffffff;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s ease;
    font-size: 0.8rem;
    display: flex;
    align-items: center;
    gap: 0.3rem;
  }

  .network-btn:hover:not(:disabled) {
    background: rgba(0, 255, 136, 0.1);
    border-color: #00ff88;
    box-shadow: 0 0 5px rgba(0, 255, 136, 0.3);
  }

  .network-btn.active {
    background: rgba(0, 255, 136, 0.2);
    border-color: #00ff88;
    box-shadow: 0 0 8px rgba(0, 255, 136, 0.5);
    color: #00ff88;
  }

  .network-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .network-switching {
    font-size: 0.7rem;
    color: #ff6b6b;
    margin-top: 0.3rem;
    text-align: center;
  }

  .nav {
    background: rgba(0, 0, 0, 0.2);
    border-bottom: 1px solid rgba(0, 255, 136, 0.3);
    padding: 1rem 2rem;
    display: flex;
    gap: 1rem;
  }

  .nav-btn {
    padding: 0.5rem 1rem;
    border: 1px solid rgba(0, 255, 136, 0.3);
    background: rgba(0, 0, 0, 0.3);
    color: #00ff88;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.3s;
    text-transform: uppercase;
    font-size: 0.9rem;
    letter-spacing: 0.5px;
  }

  .nav-btn:hover:not(:disabled) {
    background: rgba(0, 255, 136, 0.2);
    box-shadow: 0 0 10px rgba(0, 255, 136, 0.5);
  }

  .nav-btn.active {
    background: rgba(0, 255, 136, 0.2);
    border-color: #00ff88;
    box-shadow: 0 0 15px rgba(0, 255, 136, 0.6);
  }

  .nav-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .refresh-btn {
    margin-left: auto;
  }

  .owner-btn {
    background: rgba(16, 185, 129, 0.2) !important;
    border: 1px solid rgba(16, 185, 129, 0.5) !important;
    color: #10b981 !important;
  }

  .owner-btn:hover:not(:disabled) {
    background: rgba(16, 185, 129, 0.3) !important;
  }

  .main {
    padding: 2rem;
    max-width: 1200px;
    margin: 0 auto;
  }

  .dashboard h2,
  .create-game h2 {
    color: white;
    margin-bottom: 2rem;
  }

  .no-games {
    text-align: center;
    color: white;
    padding: 3rem;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 1rem;
  }

  .no-games button {
    margin-top: 1rem;
  }

  .games-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 1.5rem;
  }

  .game-card {
    background: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(10px);
    border-radius: 1rem;
    padding: 1.5rem;
    color: #00ff88;
    border: 1px solid rgba(0, 255, 136, 0.3);
    transition: all 0.3s ease;
  }

  .game-card:hover {
    border-color: #00ff88;
    box-shadow: 0 0 20px rgba(0, 255, 136, 0.3);
  }

  .game-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
  }

  .status {
    padding: 0.25rem 0.75rem;
    border-radius: 1rem;
    font-size: 0.875rem;
    font-weight: bold;
  }

  .status-0 { background: #3b82f6; }
  .status-1 { background: #f59e0b; }
  .status-2 { background: #8b5cf6; }
  .status-3 { background: #10b981; }
  .status-4 { background: #ef4444; }

  .game-info p {
    margin: 0.5rem 0;
    font-size: 0.9rem;
  }

  .game-actions {
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
  }

  .waiting-text {
    color: rgba(255, 255, 255, 0.7);
    font-style: italic;
    text-align: center;
    padding: 1rem;
  }

  .vs-contract-info {
    background: rgba(0, 255, 0, 0.1);
    padding: 1rem;
    border-radius: 0.5rem;
    margin-bottom: 1rem;
    text-align: center;
  }

  .vs-contract-info p {
    margin: 0.25rem 0;
    font-size: 0.9rem;
  }


  .form {
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border-radius: 1rem;
    padding: 2rem;
    max-width: 500px;
    margin: 0 auto;
  }

  .form-group {
    margin-bottom: 1.5rem;
  }

  .form-group label {
    display: block;
    color: white;
    margin-bottom: 0.5rem;
    font-weight: bold;
  }

  .form-group small {
    display: block;
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.8rem;
    margin-top: 0.5rem;
  }

  .mode-selector {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .mode-btn {
    flex: 1;
    padding: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 0.5rem;
    background: rgba(255, 255, 255, 0.1);
    color: white;
    cursor: pointer;
    transition: all 0.3s;
    font-size: 0.9rem;
  }

  .mode-btn:hover {
    background: rgba(255, 255, 255, 0.2);
  }

  .mode-btn.active {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
  }


  .form-input {
    width: 100%;
    padding: 0.75rem;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 0.5rem;
    background: rgba(255, 255, 255, 0.1);
    color: white;
    font-size: 1rem;
    box-sizing: border-box;
  }

  .form-input.small {
    width: auto;
    flex: 1;
  }

  .form-input::placeholder {
    color: rgba(255, 255, 255, 0.6);
  }

  .btn {
    padding: 0.75rem 1.5rem;
    border: none;
    border-radius: 0.5rem;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s;
    font-size: 1rem;
  }

  .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-primary {
    background: #3b82f6;
    color: white;
  }

  .btn-primary:hover:not(:disabled) {
    background: #2563eb;
  }

  .btn-secondary {
    background: rgba(255, 255, 255, 0.2);
    color: white;
  }

  .btn-secondary:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.3);
  }

  .btn-secondary.small {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }

  .btn-small {
    padding: 0.5rem 1rem;
    font-size: 0.875rem;
  }

  .btn-cancel {
    background: #dc2626;
    color: white;
    border: 1px solid #dc2626;
  }

  .btn-cancel:hover:not(:disabled) {
    background: #b91c1c;
    border-color: #b91c1c;
  }

  .btn-large {
    padding: 1rem 2rem;
    font-size: 1.125rem;
  }

  .guess-form,
  .reveal-form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .form-row {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .guess-form input,
  .reveal-form input {
    padding: 0.5rem;
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-radius: 0.25rem;
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }

  .help-text {
    color: rgba(255, 255, 255, 0.8);
    font-size: 0.9rem;
    margin: 1rem 0;
    padding: 1rem;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 0.5rem;
    border-left: 3px solid #3b82f6;
    word-wrap: break-word;
    overflow-wrap: break-word;
    max-width: 100%;
    box-sizing: border-box;
  }

  .help-text p {
    margin: 0.5rem 0;
    word-wrap: break-word;
    overflow-wrap: break-word;
    line-height: 1.4;
  }

  .help-text small {
    opacity: 0.8;
    display: block;
    word-wrap: break-word;
    overflow-wrap: break-word;
    line-height: 1.3;
  }

  .easy-play-section {
    background: rgba(59, 130, 246, 0.1);
    border: 1px solid rgba(59, 130, 246, 0.3);
    border-radius: 0.75rem;
    padding: 1.5rem;
    margin: 1.5rem 0;
    text-align: center;
  }

  .easy-play-section h4 {
    color: #3b82f6;
    margin: 0 0 1rem 0;
    font-size: 1.1rem;
    text-shadow: 0 0 5px rgba(59, 130, 246, 0.5);
  }

  .easy-play-buttons {
    display: flex;
    gap: 0.75rem;
    justify-content: center;
    margin-bottom: 0.75rem;
    flex-wrap: wrap;
  }

  .btn-easy {
    padding: 0.75rem 1.25rem;
    font-size: 0.95rem;
    font-weight: bold;
    border-radius: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    min-width: 140px;
    transition: all 0.3s ease;
  }

  .btn-easy:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  }

  .easy-help {
    color: rgba(59, 130, 246, 0.8);
    font-style: italic;
    font-size: 0.85rem;
  }

  .missing-values-warning {
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    border-radius: 0.5rem;
    padding: 0.75rem;
    margin: 0.75rem 0;
  }

  .missing-values-warning p {
    color: #ef4444;
    margin: 0 0 0.75rem 0;
    font-weight: bold;
  }

  .recovery-buttons {
    display: flex;
    justify-content: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
  }

  .recovery-buttons .btn {
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
    border-radius: 0.375rem;
  }

  .step-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 1rem 0;
    padding: 1rem;
    background: rgba(0, 0, 0, 0.1);
    border-radius: 0.5rem;
    flex-wrap: wrap;
    gap: 0.5rem;
    max-width: 100%;
    box-sizing: border-box;
  }

  .step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem;
    min-width: 100px;
    max-width: 120px;
    flex: 1;
    text-align: center;
  }

  .step-number {
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 0.9rem;
    background: rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.6);
  }

  .step.active .step-number {
    background: #3b82f6;
    color: white;
  }

  .step.completed .step-number {
    background: #10b981;
    color: white;
  }

  .step-text {
    font-size: 0.8rem;
    text-align: center;
    color: rgba(255, 255, 255, 0.6);
    word-wrap: break-word;
    overflow-wrap: break-word;
    line-height: 1.2;
    max-width: 100%;
  }

  .step.active .step-text {
    color: white;
    font-weight: bold;
  }

  .step.completed .step-text {
    color: rgba(255, 255, 255, 0.8);
  }

  .step-arrow {
    color: rgba(255, 255, 255, 0.4);
    font-size: 1.2rem;
    margin: 0 1rem;
  }

  .game-result {
    text-align: center;
    padding: 1rem;
  }

  .win-result {
    color: #10b981;
    font-size: 1.2rem;
    font-weight: bold;
    margin-bottom: 1rem;
  }

  .lose-result {
    color: #ef4444;
    font-size: 1.2rem;
    font-weight: bold;
    margin-bottom: 1rem;
  }

  .tie-result {
    color: #f59e0b;
    font-size: 1.2rem;
    font-weight: bold;
    margin-bottom: 1rem;
  }

  .game-details {
    background: rgba(0, 0, 0, 0.2);
    padding: 1rem;
    border-radius: 0.5rem;
  }

  .game-details p {
    margin: 0.25rem 0;
    font-size: 0.9rem;
  }

  @media (max-width: 768px) {
    .header {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      text-align: center;
    }

    .network-selector {
      justify-self: center;
      align-self: center;
    }

    .network-options {
      flex-direction: column;
      gap: 0.3rem;
    }

    .network-btn {
      font-size: 0.75rem;
      padding: 0.25rem 0.5rem;
    }

    .easy-play-buttons {
      flex-direction: column;
      align-items: center;
    }

    .btn-easy {
      width: 100%;
      max-width: 250px;
    }

    .debug-section {
      font-size: 0.75rem;
    }

    .recovery-buttons {
      flex-direction: column;
      align-items: center;
    }

    .status-grid {
      grid-template-columns: 1fr;
      gap: 0.5rem;
    }

    .waiting-reveal-section {
      padding: 1rem;
    }

    .reveal-details {
      padding: 0.75rem;
    }

    .multi-wallet-helper {
      padding: 1rem;
    }

    .quick-actions {
      flex-direction: column;
      align-items: center;
    }

    .quick-actions .btn {
      width: 100%;
      max-width: 200px;
    }

    .wallet-info {
      font-size: 0.75rem;
    }

    .benefits-list {
      gap: 0.5rem;
    }

    .ai-stats {
      gap: 0.5rem;
    }

    .first-game-options {
      flex-direction: column;
      align-items: center;
    }

    .no-games-actions {
      flex-direction: column;
      align-items: center;
    }

    .matchmaking-create-options {
      flex-direction: column;
      align-items: center;
    }

    .invitation-actions {
      align-items: stretch;
    }

    .invitation-actions .btn {
      min-width: auto;
      width: 100%;
    }

    .invite-item {
      flex-direction: column;
      align-items: stretch;
      text-align: center;
    }

    .invite-item .btn {
      margin-top: 0.5rem;
    }

    .user-info {
      flex-wrap: wrap;
      justify-content: center;
    }

    .nav {
      flex-wrap: wrap;
    }

    .main {
      padding: 1rem;
    }

    .games-grid {
      grid-template-columns: 1fr;
    }

    .form-row {
      flex-direction: column;
    }

    .form-input.small {
      width: 100%;
    }

    .mode-selector {
      flex-direction: column;
    }

    .step-indicator {
      flex-direction: column;
      gap: 1rem;
    }

    .step-arrow {
      transform: rotate(90deg);
    }

    .step {
      min-width: 200px;
      max-width: 100%;
    }

    .help-text {
      font-size: 0.85rem;
      padding: 0.75rem;
    }

    .phase-progress {
      margin-bottom: 0.5rem;
    }

    .progress-text {
      font-size: 0.8rem;
      padding: 0.4rem 0.8rem;
    }
  }


  /* Multiplayer Mission Creation Styles */
  .create-multiplayer {
    max-width: 800px;
    margin: 0 auto;
  }

  .mission-briefing {
    background: rgba(0, 255, 136, 0.1);
    border: 1px solid #00ff88;
    padding: 1.5rem;
    border-radius: 12px;
    margin-bottom: 2rem;
    text-align: center;
  }

  .mission-icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }

  .mission-config {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 2rem;
    margin-bottom: 2rem;
  }

  .config-section {
    background: rgba(255, 255, 255, 0.05);
    padding: 1.5rem;
    border-radius: 12px;
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .config-section h3 {
    color: #00ff88;
    margin-bottom: 1.5rem;
    font-size: 1.2rem;
  }

  .recruitment-info {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .info-item {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
  }

  .info-icon {
    font-size: 1.5rem;
    flex-shrink: 0;
    margin-top: 0.25rem;
  }

  .info-item div {
    flex: 1;
  }

  .info-item strong {
    color: #00ff88;
    display: block;
    margin-bottom: 0.25rem;
  }

  .info-item p {
    font-size: 0.9rem;
    color: #ccc;
    margin: 0;
  }

  .mission-actions {
    display: flex;
    gap: 1rem;
    justify-content: center;
    flex-wrap: wrap;
  }

  .btn-large {
    font-size: 1.1rem;
    padding: 1rem 2rem;
  }

  /* Page headers with live update controls */
  .practice-header,
  .multiplayer-header,
  .books-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
  }

  .practice-header h2,
  .multiplayer-header h2,
  .books-header h2 {
    margin: 0;
  }

  /* Critical Warning */
  .critical-warning {
    background: linear-gradient(135deg, rgba(255, 107, 107, 0.15) 0%, rgba(255, 69, 0, 0.15) 100%);
    border: 2px solid #ff6b6b;
    border-radius: 12px;
    padding: 1.5rem;
    margin: 1.5rem 0;
    display: flex;
    gap: 1rem;
    animation: pulse-warning 3s ease-in-out infinite;
  }

  @keyframes pulse-warning {
    0%, 100% { box-shadow: 0 0 10px rgba(255, 107, 107, 0.3); }
    50% { box-shadow: 0 0 20px rgba(255, 107, 107, 0.6), 0 0 30px rgba(255, 107, 107, 0.3); }
  }

  .warning-icon {
    font-size: 2rem;
    flex-shrink: 0;
  }

  .warning-content h4 {
    color: #ff6b6b;
    margin: 0 0 0.5rem 0;
    font-size: 1.1rem;
  }

  .warning-content p {
    margin: 0.5rem 0;
    font-weight: 500;
  }

  .warning-checklist {
    margin-top: 1rem;
    padding: 1rem;
    background: rgba(0, 0, 0, 0.2);
    border-radius: 6px;
  }

  .warning-checklist p {
    margin: 0.25rem 0;
    color: #00ff88;
  }

  /* Auto-refresh controls */
  .auto-refresh-controls {
    display: flex;
    align-items: center;
    gap: 1rem;
  }

  .auto-refresh-indicator {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .pulse-dot.disabled {
    animation: none;
    background: #666;
  }

  .btn-sm {
    font-size: 0.8rem;
    padding: 0.4rem 0.8rem;
  }

  /* Clear operations */
  .clear-operations {
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .btn-danger {
    background: rgba(255, 107, 107, 0.2);
    border: 1px solid #ff6b6b;
    color: #ff6b6b;
  }

  .btn-danger:hover {
    background: rgba(255, 107, 107, 0.3);
    border-color: #ff6b6b;
  }

  /* Instructions Section Styles */
  .instructions-section {
    max-width: 1000px;
    margin: 0 auto;
    color: white;
  }

  .instructions-header {
    text-align: center;
    margin-bottom: 2rem;
  }

  .instructions-header h2 {
    color: #00ff88;
    font-size: 2rem;
    margin-bottom: 0.5rem;
    text-shadow: 0 0 20px rgba(0, 255, 136, 0.6);
  }

  .instructions-content {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  .video-tutorial-section {
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(147, 51, 234, 0.15) 100%);
    padding: 2rem;
    border-radius: 12px;
    border: 2px solid rgba(59, 130, 246, 0.4);
    text-align: center;
  }

  .video-tutorial-section h3 {
    color: #3b82f6;
    font-size: 1.5rem;
    margin-bottom: 1rem;
    text-shadow: 0 0 10px rgba(59, 130, 246, 0.6);
  }

  .tutorial-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5rem;
  }

  .tutorial-box p {
    font-size: 1.1rem;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.9);
    max-width: 700px;
  }

  .btn-video {
    background: linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%);
    color: white;
    padding: 1rem 2rem;
    font-size: 1.1rem;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4);
  }

  .btn-video:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(59, 130, 246, 0.6);
    background: linear-gradient(135deg, #2563eb 0%, #7c3aed 100%);
  }

  .briefing-section,
  .gameplay-section,
  .strategy-section,
  .technical-section {
    background: rgba(255, 255, 255, 0.05);
    padding: 2rem;
    border-radius: 12px;
    border: 1px solid rgba(0, 255, 136, 0.2);
  }

  .gameplay-section {
    text-align: center;
  }

  .gameplay-section p,
  .gameplay-section li {
    font-size: 1.1rem;
    line-height: 1.6;
  }

  .gameplay-section h4 {
    font-size: 1.3rem;
  }

  .briefing-section h3,
  .gameplay-section h3,
  .strategy-section h3,
  .technical-section h3 {
    color: #00ff88;
    font-size: 1.5rem;
    margin-bottom: 1rem;
    text-shadow: 0 0 10px rgba(0, 255, 136, 0.4);
  }

  .rewards-highlight {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
    margin: 1.5rem 0;
  }

  .reward-item {
    background: linear-gradient(135deg, rgba(0, 255, 136, 0.1), rgba(0, 255, 136, 0.05));
    padding: 1.5rem;
    border-radius: 10px;
    border: 1px solid rgba(0, 255, 136, 0.3);
    display: flex;
    gap: 1rem;
    align-items: flex-start;
  }

  .reward-icon {
    font-size: 2.5rem;
    flex-shrink: 0;
  }

  .reward-text strong {
    color: #00ff88;
    font-size: 1.2rem;
    display: block;
    margin-bottom: 0.5rem;
  }

  .reward-text p {
    margin: 0;
    line-height: 1.5;
  }

  .step-list {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    align-items: center;
  }

  .step {
    display: flex;
    gap: 1.5rem;
    align-items: flex-start;
    max-width: 600px;
    text-align: left;
  }

  .step-number {
    background: linear-gradient(135deg, #00ff88, #00cc6a);
    color: #000;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 1.2rem;
    flex-shrink: 0;
    box-shadow: 0 0 15px rgba(0, 255, 136, 0.5);
  }

  .step-content h4 {
    color: #00ff88;
    margin-bottom: 0.5rem;
    font-size: 1.1rem;
  }

  .step-content ul {
    margin: 0.5rem 0;
    padding-left: 1.5rem;
  }

  .step-content li {
    margin-bottom: 0.5rem;
  }

  .strategy-tips {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1rem;
    margin-top: 1rem;
  }

  .tip {
    background: rgba(0, 255, 136, 0.05);
    padding: 1rem;
    border-radius: 8px;
    border-left: 3px solid #00ff88;
    display: flex;
    gap: 1rem;
    align-items: flex-start;
  }

  .tip-icon {
    font-size: 1.5rem;
    flex-shrink: 0;
  }

  .tip p {
    margin: 0;
    line-height: 1.4;
  }

  .tech-list {
    list-style: none;
    padding: 0;
  }

  .tech-list li {
    background: rgba(0, 255, 136, 0.05);
    padding: 1rem;
    margin-bottom: 0.5rem;
    border-radius: 8px;
    border-left: 3px solid #00ff88;
  }

  .chronicles-promo {
    margin-top: 2rem;
  }

  .promo-box {
    background: linear-gradient(135deg, rgba(255, 107, 107, 0.1), rgba(255, 107, 107, 0.05));
    border: 1px solid rgba(255, 107, 107, 0.3);
    padding: 2rem;
    border-radius: 12px;
    text-align: center;
    position: relative;
    overflow: hidden;
  }

  .promo-box::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, transparent 48%, rgba(255, 107, 107, 0.1) 49%, rgba(255, 107, 107, 0.1) 51%, transparent 52%);
    pointer-events: none;
  }

  .promo-box h3 {
    color: #ff6b6b;
    font-size: 1.5rem;
    margin-bottom: 1rem;
    text-shadow: 0 0 10px rgba(255, 107, 107, 0.4);
  }

  .chronicles-btn {
    background: linear-gradient(135deg, #ff6b6b, #cc5555);
    border: none;
    color: white;
    font-weight: bold;
    padding: 1rem 2rem;
    font-size: 1.1rem;
    margin-top: 1rem;
    transition: all 0.3s ease;
  }

  .chronicles-btn:hover {
    background: linear-gradient(135deg, #cc5555, #aa4444);
    box-shadow: 0 0 20px rgba(255, 107, 107, 0.6);
    transform: translateY(-2px);
  }

  .classified-stamp {
    position: absolute;
    top: 1rem;
    right: 1rem;
    background: rgba(255, 107, 107, 0.2);
    border: 2px solid #ff6b6b;
    color: #ff6b6b;
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
    font-weight: bold;
    transform: rotate(15deg);
    border-radius: 4px;
    text-shadow: none;
  }

  @media (max-width: 768px) {
    .instructions-content {
      padding: 0 1rem;
    }

    .briefing-section,
    .gameplay-section,
    .strategy-section,
    .technical-section {
      padding: 1.5rem;
    }

    .rewards-highlight {
      grid-template-columns: 1fr;
    }

    .step {
      flex-direction: column;
      align-items: center;
      text-align: center;
    }

    .strategy-tips {
      grid-template-columns: 1fr;
    }

    .classified-stamp {
      position: static;
      transform: none;
      margin-top: 1rem;
      display: inline-block;
    }

    .mission-config {
      grid-template-columns: 1fr;
    }

    .mission-actions {
      flex-direction: column;
    }
  }
`;

// Inject styles
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default App;
